import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../../contexts/AuthContext';
import MediaLibraryPickerModal from '../Website/MediaLibraryPickerModal';

const scheduleStatusMap = {
  pending: { label: 'Pending', tone: 'badge-muted' },
  in_progress: { label: 'On air', tone: 'badge-success' },
  completed: { label: 'Completed', tone: 'badge-muted' },
  cancelled: { label: 'Cancelled', tone: 'badge-danger' },
  failed: { label: 'Failed', tone: 'badge-danger' },
};

const programCategoryOptions = [
  { value: 'sermons', label: 'Sermons' },
  { value: 'music_videos', label: 'Music videos' },
  { value: 'movies', label: 'Movies' },
  { value: 'filler', label: 'Filler' },
  { value: 'other', label: 'Other' },
];
const fillerCategoryOptions = [
  { value: 'any', label: 'Any category' },
  ...programCategoryOptions,
];
const programSortOptions = [
  { value: 'title-asc', label: 'Title A-Z' },
  { value: 'title-desc', label: 'Title Z-A' },
  { value: 'uploaded-desc', label: 'Uploaded (newest)' },
  { value: 'uploaded-asc', label: 'Uploaded (oldest)' },
];

const PUSHER_KEY = process.env.REACT_APP_PUSHER_KEY || '';
const PUSHER_CLUSTER = process.env.REACT_APP_PUSHER_CLUSTER || 'mt1';
const PUSHER_HOST = process.env.REACT_APP_PUSHER_HOST;
const PUSHER_PORT = process.env.REACT_APP_PUSHER_PORT;
const PUSHER_SCHEME = process.env.REACT_APP_PUSHER_SCHEME || 'https';
const SCHEDULE_CHANNEL = process.env.REACT_APP_PUSHER_SCHEDULE_CHANNEL || 'programming.schedule';

const toDateTimeLocal = (date) => {
  const pad = (value, size = 2) => String(value).padStart(size, '0');
  const year = date.getFullYear();
  const month = pad(date.getMonth() + 1);
  const day = pad(date.getDate());
  const hours = pad(date.getHours());
  const minutes = pad(date.getMinutes());
  const seconds = pad(date.getSeconds());
  const milliseconds = pad(date.getMilliseconds(), 3);
  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.${milliseconds}`;
};

const startOfWeek = (date) => {
  const clone = new Date(date);
  const day = clone.getDay();
  const diff = clone.getDate() - day + (day === 0 ? -6 : 1);
  clone.setDate(diff);
  clone.setHours(0, 0, 0, 0);
  return clone;
};

const addDays = (date, days) => {
  const clone = new Date(date);
  clone.setDate(clone.getDate() + days);
  return clone;
};

const getCalendarRange = (anchor = new Date()) => {
  const start = startOfWeek(anchor);
  const end = addDays(start, 6);
  end.setHours(23, 59, 59, 999);
  return { start, end };
};

const toIsoString = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toISOString();
};

const formatRangeLabel = (range) => {
  const formatter = new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
  });
  return `${formatter.format(range.start)} – ${formatter.format(range.end)}`;
};

const formatTimeRange = (start, end) => {
  const formatter = new Intl.DateTimeFormat(undefined, {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    fractionalSecondDigits: 3,
  });
  return `${formatter.format(new Date(start))} → ${formatter.format(new Date(end))}`;
};

const formatDateRange = (start, end) => {
  const formatter = new Intl.DateTimeFormat(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
  const startDate = new Date(start);
  const endDate = new Date(end);
  const startLabel = formatter.format(startDate);
  const endLabel = formatter.format(endDate);
  return startLabel === endLabel ? startLabel : `${startLabel} → ${endLabel}`;
};

const formatDuration = (totalSeconds) => {
  if (!Number.isFinite(totalSeconds)) return '—';
  const clamped = Math.max(0, Math.floor(totalSeconds));
  const hours = Math.floor(clamped / 3600);
  const minutes = Math.floor((clamped % 3600) / 60);
  const seconds = clamped % 60;
  const pad = (value) => String(value).padStart(2, '0');
  return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
};

const parseDurationHms = (value) => {
  if (!value) return null;
  const parts = value.split(':').map((part) => part.trim());
  if (parts.length !== 3) return null;
  if (parts.some((part) => !/^\d+$/.test(part))) return null;

  const [hours, minutes, seconds] = parts.map(Number);
  if (minutes > 59 || seconds > 59) return null;
  return hours * 3600 + minutes * 60 + seconds;
};

const formatDateTime = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  const formatter = new Intl.DateTimeFormat(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
  return formatter.format(date);
};

const formatCategoryLabel = (value) => {
  const match = programCategoryOptions.find((option) => option.value === value);
  return match ? match.label : 'Other';
};

const getDurationVariance = (seconds, targetSeconds, toleranceSeconds) => {
  if (!Number.isFinite(seconds)) return null;
  const delta = seconds - targetSeconds;
  const diff = Math.abs(delta);
  if (diff <= toleranceSeconds) return null;
  return {
    delta,
    diff,
    label: delta < 0 ? 'Short' : 'Long',
  };
};

const buildCalendarDays = (range) => {
  const formatter = new Intl.DateTimeFormat(undefined, { weekday: 'short' });
  return Array.from({ length: 7 }).map((_, index) => {
    const date = addDays(range.start, index);
    return {
      date,
      dateKey: date.toISOString().slice(0, 10),
      label: formatter.format(date),
      day: date.getDate(),
    };
  });
};

const defaultScheduleForm = () => {
  const start = new Date();
  start.setMinutes(start.getMinutes() + 10);
  const end = new Date(start.getTime() + 15 * 60_000);

  return {
    type: 'program',
    title: '',
    program_id: '',
    starts_at: toDateTimeLocal(start),
    ends_at: toDateTimeLocal(end),
    live_input_url: '',
    youtube_url: '',
    jitsi_room: '',
    jitsi_join_url: '',
    obs_scene: '',
    obs_profile: '',
    recurrence_type: 'none',
    recurrence_count: 1,
    auto_filler_enabled: true,
    auto_filler_category: 'any',
    auto_filler_tags: '',
  };
};

const defaultProgramForm = () => ({
  title: '',
  description: '',
  category: 'other',
  media_type: 'video',
  source_url: '',
  thumbnail_url: '',
  duration_seconds: '',
  duration_hms: '',
  metadata: {},
  usage: 'primary',
  filler_tags: '',
  is_loopable: false,
});

const defaultArchiveChannelForm = () => ({
  name: 'Main TV',
  slug: 'main',
  fallback_mode: 'archive_shuffle',
  is_enabled: true,
  shuffle_lookahead_count: 10,
  shuffle_avoid_repeat_hours: 24,
});

const defaultArchiveSourceForm = () => ({
  channel_id: '',
  name: '',
  query: '',
  weight: 1,
});

const defaultArchivePreviewForm = () => ({
  channel_id: '',
  count: 10,
  seed: '',
  debug: false,
  exclude_recent: true,
  min_duration_seconds: '',
  max_duration_seconds: '',
  license_allowlist: '',
});

const buildArchivePreviewItemKey = (item, index) =>
  `${item?.identifier || ''}::${item?.file_name || ''}::${index}`;

const formatApiError = (error, fallbackMessage) => {
  const payload = error?.response?.data;
  if (payload?.errors && typeof payload.errors === 'object') {
    const firstFieldErrors = Object.values(payload.errors).find(
      (entry) => Array.isArray(entry) && entry.length > 0
    );
    if (firstFieldErrors) {
      return firstFieldErrors.join(' ');
    }
  }

  if (typeof payload?.message === 'string' && payload.message.trim()) {
    return payload.message;
  }

  if (typeof error?.message === 'string' && error.message.trim()) {
    return error.message;
  }

  return fallbackMessage;
};

const toOdyseeEmbed = (url) => {
  if (!url) return null;
  try {
    const normalized = url.startsWith('lbry://') ? url.replace('lbry://', 'https://') : url;
    const parsed = new URL(normalized);
    const host = parsed.hostname.toLowerCase();
    const isOdysee = host.includes('odysee.com') || host.includes('lbry.tv');
    if (!isOdysee) return null;

    const segments = parsed.pathname.split('/').filter(Boolean);
    if (!segments.length) return null;

    const path = segments.map((s) => decodeURIComponent(s)).join('/');
    if (path.startsWith('$/embed')) {
      return `https://odysee.com/${path}`;
    }

    return `https://odysee.com/$/embed/${path}`;
  } catch (_) {
    return null;
  }
};

const parseYouTubeStartSeconds = (rawValue) => {
  if (!rawValue) return null;
  if (/^\d+$/.test(rawValue)) {
    return Number(rawValue);
  }

  const match = rawValue.match(/^(?:(\d+)h)?(?:(\d+)m)?(?:(\d+)s)?$/i);
  if (!match) return null;

  const hours = Number(match[1] || 0);
  const minutes = Number(match[2] || 0);
  const seconds = Number(match[3] || 0);
  const total = hours * 3600 + minutes * 60 + seconds;

  return total > 0 ? total : null;
};

const toYouTubeEmbed = (url) => {
  if (!url) return null;
  try {
    const normalized = /^https?:\/\//i.test(url) ? url : `https://${url}`;
    const parsed = new URL(normalized);
    const host = parsed.hostname.toLowerCase();

    let videoId = '';
    if (host === 'youtu.be') {
      videoId = parsed.pathname.split('/').filter(Boolean)[0] || '';
    } else if (host.includes('youtube.com')) {
      const segments = parsed.pathname.split('/').filter(Boolean);
      if (parsed.pathname === '/watch') {
        videoId = parsed.searchParams.get('v') || '';
      } else if (segments[0] === 'embed' || segments[0] === 'shorts' || segments[0] === 'live') {
        videoId = segments[1] || '';
      }
    }

    if (!videoId) return null;

    const startRaw = parsed.searchParams.get('t') || parsed.searchParams.get('start');
    const startSeconds = parseYouTubeStartSeconds(startRaw);
    const startQuery = startSeconds ? `?start=${startSeconds}` : '';

    return `https://www.youtube.com/embed/${encodeURIComponent(videoId)}${startQuery}`;
  } catch (_) {
    return null;
  }
};

const parseArchiveMp4Url = (url) => {
  if (!url) return { kind: 'invalid', message: 'Paste an archive.org MP4 URL.' };
  try {
    const parsed = new URL(url);
    const host = parsed.hostname.toLowerCase();
    if (!host.includes('archive.org')) {
      return { kind: 'invalid', message: 'Archive.org URLs must be on archive.org.' };
    }
    if (parsed.pathname.startsWith('/details/')) {
      const segments = parsed.pathname.split('/').filter(Boolean);
      const identifier = segments[1] || '';
      if (!identifier) {
        return { kind: 'invalid', message: 'Archive.org details URLs must include an identifier.' };
      }
      return { kind: 'details', identifier };
    }
    if (parsed.pathname.startsWith('/download/')) {
      const segments = parsed.pathname.split('/').filter(Boolean);
      const identifier = segments[1] || '';
      if (!identifier) {
        return { kind: 'invalid', message: 'Archive.org download URLs must include an identifier.' };
      }
      return { kind: 'download', identifier, url };
    }
    if (!parsed.pathname.toLowerCase().endsWith('.mp4')) {
      return { kind: 'invalid', message: 'Archive.org URL must end with .mp4.' };
    }
    return { kind: 'direct', url };
  } catch (_) {
    return { kind: 'invalid', message: 'Paste a valid archive.org MP4 URL.' };
  }
};

const normalizeArchiveUrl = (url) => {
  try {
    const parsed = new URL(url);
    const host = parsed.hostname.toLowerCase();
    if (!host.includes('archive.org')) return url;
    const segments = parsed.pathname.split('/').map((segment) => {
      if (segment === '') return '';
      return encodeURIComponent(decodeURIComponent(segment));
    });
    parsed.pathname = segments.join('/');
    return parsed.toString();
  } catch (_) {
    return url;
  }
};

const ActionsDropdown = ({ actions, label = 'Actions' }) => {
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;

    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const handleAction = (action) => {
    action.onClick();
    setOpen(false);
  };

  return (
    <div className="actions-dropdown" ref={menuRef}>
      <button type="button" className="btn btn-muted" onClick={() => setOpen((prev) => !prev)}>
        {label} ▾
      </button>
      {open && (
        <div className="actions-dropdown-menu">
          {actions.map((action) => (
            <button
              key={action.label}
              type="button"
              className={`actions-dropdown-item ${action.variant === 'danger' ? 'danger' : ''}`}
              onClick={() => handleAction(action)}
              disabled={action.disabled}
            >
              {action.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const ProgrammingWorkspace = ({ defaultTab = 'schedule' }) => {
  const { isSuperAdmin } = useAuth();
  const [programOptions, setProgramOptions] = useState([]);
  const [libraryPrograms, setLibraryPrograms] = useState([]);
  const [programFilters, setProgramFilters] = useState({ category: 'all', search: '', sort: 'title-asc' });
  const [programPagination, setProgramPagination] = useState({
    page: 1,
    perPage: 50,
    total: 0,
    lastPage: 1,
    from: 0,
    to: 0,
  });
  const [scheduleItems, setScheduleItems] = useState([]);
  const [scheduleWindow, setScheduleWindow] = useState(null);
  const [serverTime, setServerTime] = useState(null);
  const [serverTimezone, setServerTimezone] = useState(null);
  const [serverOffsetMs, setServerOffsetMs] = useState(null);
  const [scheduleNow, setScheduleNow] = useState(() => new Date());
  const [programForm, setProgramForm] = useState(defaultProgramForm);
  const [scheduleForm, setScheduleForm] = useState(defaultScheduleForm);
  const [odyseeUrl, setOdyseeUrl] = useState('');
  const [youtubeUrl, setYoutubeUrl] = useState('');
  const [odyseeLookup, setOdyseeLookup] = useState({ loading: false });
  const [archiveUrl, setArchiveUrl] = useState('');
  const [archiveIdentifier, setArchiveIdentifier] = useState('');
  const [archiveFileName, setArchiveFileName] = useState('');
  const [archivePickIndex, setArchivePickIndex] = useState('');
  const [archiveLookup, setArchiveLookup] = useState({
    loading: false,
    identifier: '',
    title: '',
    files: [],
  });
  const [archiveChannels, setArchiveChannels] = useState([]);
  const [archiveSources, setArchiveSources] = useState([]);
  const [selectedArchiveSourceIds, setSelectedArchiveSourceIds] = useState([]);
  const [bulkDeletingArchiveSources, setBulkDeletingArchiveSources] = useState(false);
  const [archiveOwnUploader, setArchiveOwnUploader] = useState('');
  const [archiveChannelForm, setArchiveChannelForm] = useState(defaultArchiveChannelForm);
  const [editingArchiveChannel, setEditingArchiveChannel] = useState(null);
  const [archiveSourceForm, setArchiveSourceForm] = useState(defaultArchiveSourceForm);
  const [editingArchiveSource, setEditingArchiveSource] = useState(null);
  const [archivePreviewForm, setArchivePreviewForm] = useState(defaultArchivePreviewForm);
  const [archivePreview, setArchivePreview] = useState({ loading: false, items: [], meta: null });
  const [selectedArchivePreviewItemKeys, setSelectedArchivePreviewItemKeys] = useState([]);
  const [archiveQueueStartAt, setArchiveQueueStartAt] = useState(() => {
    const start = new Date();
    start.setMinutes(start.getMinutes() + 5);
    start.setSeconds(0, 0);
    return toDateTimeLocal(start);
  });
  const [queueingArchivePreview, setQueueingArchivePreview] = useState(false);
  const [archiveQueueConfirmation, setArchiveQueueConfirmation] = useState({
    open: false,
    afterLastScheduled: false,
    startAt: null,
    items: [],
  });
  const [archiveHistory, setArchiveHistory] = useState({
    loading: false,
    items: [],
    total: 0,
  });
  const [loading, setLoading] = useState({ programs: true, programOptions: true, schedule: true });
  const [saving, setSaving] = useState({ program: false, schedule: false });
  const [feedback, setFeedback] = useState({ program: '', schedule: '', error: '' });
  const targetDurationSeconds = 3600;
  const durationToleranceSeconds = 15;
  const [editingProgram, setEditingProgram] = useState(null);
  const [previewProgram, setPreviewProgram] = useState(null);
  const [programThumbnailPickerOpen, setProgramThumbnailPickerOpen] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState(null);
  const [viewMode, setViewMode] = useState('list');
  const [manualStart, setManualStart] = useState({
    target: null,
    starts_at: toDateTimeLocal(new Date()),
    immediate: false,
    force: false,
    loading: false,
    error: '',
    current: null,
  });
  const [calendarRange, setCalendarRange] = useState(() => getCalendarRange(new Date()));
  const calendarRangeRef = useRef(calendarRange);
  const previewChannelId = Number(archivePreviewForm.channel_id || 0);
  const previewActiveSourceCount = archiveSources.filter(
    (source) => source.is_active && Number(source.channel_id) === previewChannelId
  ).length;
  const archivePreviewSkippedTotals = archivePreview.meta?.debug?.totals?.skipped ?? {};
  const archivePreviewRecentlyPlayedSkipped = Number(
    archivePreviewSkippedTotals?.recently_played ?? 0
  );
  const archivePreviewLikelyRecentFilterBlock =
    archivePreview.items.length === 0 &&
    Boolean(archivePreview.meta?.options?.exclude_recent) &&
    archivePreviewRecentlyPlayedSkipped > 0;
  const selectedArchivePreviewCount = selectedArchivePreviewItemKeys.length;
  const allArchivePreviewItemsSelected =
    archivePreview.items.length > 0 &&
    selectedArchivePreviewCount === archivePreview.items.length;
  const queuePlanLastItem =
    archiveQueueConfirmation.items.length > 0
      ? archiveQueueConfirmation.items[archiveQueueConfirmation.items.length - 1]
      : null;

  useEffect(() => {
    calendarRangeRef.current = calendarRange;
  }, [calendarRange]);

  const loadPrograms = useCallback(
    async (overrides = {}) => {
      const nextPage = overrides.page ?? programPagination.page;
      const nextPerPage = overrides.perPage ?? programPagination.perPage;
      const nextCategory = overrides.category ?? programFilters.category;

      try {
        setLoading((prev) => ({ ...prev, programs: true }));
        const { data } = await axios.get('/admin/programming/programs', {
          params: {
            per_page: nextPerPage,
            page: nextPage,
            active_only: 1,
            ...(nextCategory !== 'all' ? { category: nextCategory } : {}),
          },
        });
        const items = data.data ?? data;
        setLibraryPrograms(items);

        if (data?.current_page) {
          setProgramPagination({
            page: data.current_page,
            perPage: data.per_page,
            total: data.total,
            lastPage: data.last_page,
            from: data.from ?? 0,
            to: data.to ?? 0,
          });
        } else {
          const total = Array.isArray(items) ? items.length : 0;
          setProgramPagination((prev) => ({
            ...prev,
            page: nextPage,
            perPage: nextPerPage,
            total,
            lastPage: 1,
            from: total ? 1 : 0,
            to: total,
          }));
        }
      } catch (error) {
        setFeedback((prev) => ({
          ...prev,
          error: error.response?.data?.message ?? 'Unable to load programs.',
        }));
      } finally {
        setLoading((prev) => ({ ...prev, programs: false }));
      }
    },
    [programFilters.category, programPagination.page, programPagination.perPage]
  );

  const loadProgramOptions = useCallback(async () => {
    try {
      setLoading((prev) => ({ ...prev, programOptions: true }));
      const { data } = await axios.get('/admin/programming/programs', {
        params: { per_page: 500, active_only: 1 },
      });
      setProgramOptions(data.data ?? data);
    } catch (error) {
      setFeedback((prev) => ({
        ...prev,
        error: error.response?.data?.message ?? 'Unable to load program options.',
      }));
    } finally {
      setLoading((prev) => ({ ...prev, programOptions: false }));
    }
  }, []);

  const loadSchedule = useCallback(async (range) => {
    const targetRange = range ?? calendarRangeRef.current;
    if (!targetRange) return;

    try {
      setLoading((prev) => ({ ...prev, schedule: true }));
      const { data } = await axios.get('/admin/programming/schedule/timeline', {
        params: {
          start: targetRange.start.toISOString(),
          end: targetRange.end.toISOString(),
        },
      });
      setScheduleItems(data.items ?? []);
      setScheduleWindow(data.window ?? null);
      setServerTime(data.server_time ?? null);
      setServerTimezone(data.server_timezone ?? null);
      if (data.server_time) {
        const serverMs = new Date(data.server_time).getTime();
        if (!Number.isNaN(serverMs)) {
          setServerOffsetMs(serverMs - Date.now());
        }
      }
    } catch (error) {
      setFeedback((prev) => ({
        ...prev,
        error: error.response?.data?.message ?? 'Unable to load schedule.',
      }));
    } finally {
      setLoading((prev) => ({ ...prev, schedule: false }));
    }
  }, []);

  const loadPlaybackState = useCallback(async () => {
    try {
      const { data } = await axios.get('/tv/playback-state');
      setManualStart((prev) => ({ ...prev, current: data.current ?? null }));
    } catch (error) {
      setManualStart((prev) => ({
        ...prev,
        error: prev.error || error.response?.data?.message || 'Unable to load playback status.',
      }));
    }
  }, []);

  const loadArchiveHistory = useCallback(async (channelId = null) => {
    try {
      setArchiveHistory((prev) => ({ ...prev, loading: true }));
      const params = {
        per_page: 20,
      };
      if (channelId) {
        params.channel_id = Number(channelId);
      }

      const { data } = await axios.get('/admin/programming/archive/history', { params });
      const items = Array.isArray(data?.data) ? data.data : [];
      setArchiveHistory({
        loading: false,
        items,
        total: Number(data?.total || items.length || 0),
      });
    } catch (error) {
      setArchiveHistory((prev) => ({ ...prev, loading: false }));
      setFeedback((prev) => ({
        ...prev,
        error: formatApiError(error, 'Unable to load archive shuffle history.'),
      }));
    }
  }, []);

  const loadArchiveConfig = useCallback(async () => {
    try {
      const [channelsResponse, sourcesResponse] = await Promise.all([
        axios.get('/admin/programming/archive/channels', { params: { per_page: 100 } }),
        axios.get('/admin/programming/archive/sources', { params: { per_page: 200 } }),
      ]);

      const channels = channelsResponse.data?.data ?? channelsResponse.data ?? [];
      const sources = sourcesResponse.data?.data ?? sourcesResponse.data ?? [];
      setArchiveChannels(channels);
      setArchiveSources(sources);
      setSelectedArchiveSourceIds((prev) => {
        const validIds = new Set(sources.map((source) => source.id));
        return prev.filter((id) => validIds.has(id));
      });

      if (channels.length > 0) {
        const fallbackChannelId = String(channels[0].id);
        setArchiveSourceForm((prev) => ({
          ...prev,
          channel_id: prev.channel_id || fallbackChannelId,
        }));
        setArchivePreviewForm((prev) => ({
          ...prev,
          channel_id: prev.channel_id || fallbackChannelId,
        }));
        await loadArchiveHistory(fallbackChannelId);
      } else {
        await loadArchiveHistory(null);
      }
    } catch (error) {
      setFeedback((prev) => ({
        ...prev,
        error: error.response?.data?.message ?? 'Unable to load archive settings.',
      }));
    }
  }, [loadArchiveHistory]);

  useEffect(() => {
    loadPrograms();
  }, [loadPrograms]);

  useEffect(() => {
    loadProgramOptions();
  }, [loadProgramOptions]);

  useEffect(() => {
    loadArchiveConfig();
  }, [loadArchiveConfig]);

  useEffect(() => {
    if (!archivePreviewForm.channel_id) return;
    loadArchiveHistory(archivePreviewForm.channel_id);
  }, [archivePreviewForm.channel_id, loadArchiveHistory]);

  useEffect(() => {
    setSelectedArchivePreviewItemKeys([]);
    setArchiveQueueConfirmation({
      open: false,
      afterLastScheduled: false,
      startAt: null,
      items: [],
    });
  }, [archivePreviewForm.channel_id]);

  useEffect(() => {
    loadSchedule(calendarRange);
  }, [calendarRange, loadSchedule]);

  useEffect(() => {
    if (!PUSHER_KEY || typeof window === 'undefined' || !window.Pusher) {
      return undefined;
    }

    const options = {
      cluster: PUSHER_CLUSTER,
      forceTLS: PUSHER_SCHEME !== 'http',
    };

    if (PUSHER_HOST) {
      options.wsHost = PUSHER_HOST;
    }
    if (PUSHER_PORT) {
      options.wsPort = Number(PUSHER_PORT);
    }
    if (PUSHER_SCHEME) {
      options.wsScheme = PUSHER_SCHEME;
    }

    const pusher = new window.Pusher(PUSHER_KEY, options);
    const channel = pusher.subscribe(SCHEDULE_CHANNEL);
    const handler = () => loadSchedule();
    channel.bind('ProgramScheduleUpdated', handler);

    return () => {
      channel.unbind('ProgramScheduleUpdated', handler);
      pusher.unsubscribe(SCHEDULE_CHANNEL);
      pusher.disconnect();
    };
  }, [loadSchedule]);

  useEffect(() => {
    const timer = setInterval(() => {
      const now = Date.now();
      setScheduleNow(new Date(serverOffsetMs == null ? now : now + serverOffsetMs));
    }, 1000);

    return () => clearInterval(timer);
  }, [serverOffsetMs]);

  const resolveSlotStatus = useCallback(
    (slot) => {
      if (!slot || slot.status !== 'pending') return slot?.status;
      const start = new Date(slot.starts_at);
      const end = new Date(slot.ends_at);
      if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return slot.status;
      if (scheduleNow >= start && scheduleNow <= end) {
        return 'in_progress';
      }
      return slot.status;
    },
    [scheduleNow]
  );

  useEffect(() => {
    if (manualStart.target) {
      loadPlaybackState();
    }
  }, [manualStart.target, loadPlaybackState]);

  const resetProgramForm = () => {
    setProgramForm(defaultProgramForm());
    setOdyseeUrl('');
    setYoutubeUrl('');
    setArchiveUrl('');
    setArchiveIdentifier('');
    setArchiveFileName('');
    setArchivePickIndex('');
    setArchiveLookup({ loading: false, identifier: '', title: '', files: [] });
    setEditingProgram(null);
  };

  const resetScheduleForm = (preserveType = true) => {
    setScheduleForm((prev) => {
      const base = defaultScheduleForm();
      if (preserveType) {
        base.type = prev.type;
        base.recurrence_type = prev.recurrence_type;
        base.recurrence_count = prev.recurrence_count;
      }
      return base;
    });
    setEditingSchedule(null);
  };

  const handleProgramChange = (event) => {
    const { name, value, type, checked } = event.target;
    if (name === 'duration_hms') {
      const parsed = parseDurationHms(value);
      setProgramForm((prev) => ({
        ...prev,
        duration_hms: value,
        duration_seconds:
          value === '' ? '' : parsed != null ? String(parsed) : prev.duration_seconds,
      }));
      return;
    }

    setProgramForm((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleProgramCategoryFilter = (event) => {
    const nextCategory = event.target.value;
    setProgramFilters((prev) => ({ ...prev, category: nextCategory }));
    setProgramPagination((prev) => ({ ...prev, page: 1 }));
    loadPrograms({ page: 1, category: nextCategory });
  };

  const handleProgramsPerPageChange = (event) => {
    const nextPerPage = Number(event.target.value) || 25;
    setProgramPagination((prev) => ({ ...prev, perPage: nextPerPage, page: 1 }));
    loadPrograms({ page: 1, perPage: nextPerPage });
  };

  const handleProgramsPageChange = (nextPage) => {
    if (nextPage < 1 || nextPage > programPagination.lastPage) return;
    setProgramPagination((prev) => ({ ...prev, page: nextPage }));
    loadPrograms({ page: nextPage });
  };

  const handleOdyseeConvert = async () => {
    const embedUrl = toOdyseeEmbed(odyseeUrl);
    if (!embedUrl) {
      setFeedback((prev) => ({
        ...prev,
        error:
          'Paste a valid Odysee link (https://odysee.com/@channel/video:hash or lbry://@channel/video:hash).',
      }));
      return;
    }

    setFeedback((prev) => ({ ...prev, error: '', program: '' }));
    setOdyseeLookup({ loading: true });

    try {
      const { data } = await axios.post('/admin/programming/odysee/resolve', { url: odyseeUrl });
      const durationSeconds = data?.duration_seconds;
      const thumbnailUrl = data?.thumbnail_url;

      setProgramForm((prev) => ({
        ...prev,
        media_type: 'web',
        source_url: embedUrl,
        thumbnail_url: thumbnailUrl || prev.thumbnail_url,
        metadata: {
          ...(prev.metadata || {}),
          odysee_url: odyseeUrl,
        },
        duration_seconds: Number.isFinite(durationSeconds) ? String(durationSeconds) : prev.duration_seconds,
        duration_hms: Number.isFinite(durationSeconds) ? formatDuration(durationSeconds) : prev.duration_hms,
      }));

      if (Number.isFinite(durationSeconds)) {
        setFeedback((prev) => ({
          ...prev,
          program: `Odysee link applied. Duration set to ${formatDuration(durationSeconds)}.`,
        }));
      } else {
        setFeedback((prev) => ({
          ...prev,
          program: 'Odysee link applied. Duration unavailable; enter it manually if needed.',
        }));
      }
    } catch (error) {
      setFeedback((prev) => ({
        ...prev,
        error: error.response?.data?.message ?? 'Could not fetch Odysee duration.',
      }));
    } finally {
      setOdyseeLookup({ loading: false });
    }
  };

  const handleYouTubeApply = () => {
    const embedUrl = toYouTubeEmbed(youtubeUrl);
    if (!embedUrl) {
      setFeedback((prev) => ({
        ...prev,
        error: 'Paste a valid YouTube link (youtube.com/watch, youtu.be, shorts, or live).',
      }));
      return;
    }

    setFeedback((prev) => ({ ...prev, error: '', program: '' }));
    setProgramForm((prev) => ({
      ...prev,
      media_type: 'web',
      source_url: embedUrl,
      metadata: {
        ...(prev.metadata || {}),
        youtube_url: youtubeUrl,
      },
    }));
    setFeedback((prev) => ({
      ...prev,
      program: 'YouTube link applied. Source URL set to embeddable player.',
    }));
  };

  const handleArchiveApply = () => {
    const trimmedUrl = archiveUrl.trim();
    const result = parseArchiveMp4Url(trimmedUrl);
    if (result.kind === 'invalid') {
      setFeedback((prev) => ({ ...prev, error: result.message, program: '' }));
      setArchiveIdentifier('');
      return;
    }

    if (result.kind === 'details') {
      setArchiveIdentifier(result.identifier);
      setArchivePickIndex('');
      setArchiveFileName('');
      setArchiveLookup({ loading: false, identifier: '', title: '', files: [] });
      setFeedback((prev) => ({
        ...prev,
        error: '',
        program: 'Details link detected. Enter the file name to build the MP4 URL.',
      }));
      return;
    }

    setArchiveIdentifier('');
    setArchiveFileName('');
    setArchivePickIndex('');
    setArchiveLookup({ loading: false, identifier: '', title: '', files: [] });
    setFeedback((prev) => ({ ...prev, error: '', program: '' }));
    const normalizedUrl = normalizeArchiveUrl(result.url);
    setProgramForm((prev) => ({
      ...prev,
      media_type: 'video',
      source_url: normalizedUrl,
      metadata: {
        ...(prev.metadata || {}),
        archive_url: normalizedUrl,
      },
    }));
    setFeedback((prev) => ({
      ...prev,
      program: 'Archive.org MP4 applied.',
    }));
  };

  const handleArchiveFetchFiles = async () => {
    const trimmedUrl = archiveUrl.trim();
    let identifier = archiveIdentifier.trim();
    if (!identifier && trimmedUrl) {
      const result = parseArchiveMp4Url(trimmedUrl);
      if (result.kind === 'details' || result.kind === 'download') {
        identifier = result.identifier;
        setArchiveIdentifier(result.identifier);
      } else if (result.kind === 'invalid') {
        setFeedback((prev) => ({ ...prev, error: result.message, program: '' }));
        return;
      }
    }

    if (!identifier && !trimmedUrl) {
      setFeedback((prev) => ({ ...prev, error: 'Add an Archive.org identifier first.', program: '' }));
      return;
    }

    setFeedback((prev) => ({ ...prev, error: '', program: '' }));
    setArchiveLookup((prev) => ({ ...prev, loading: true, files: [], identifier, title: '' }));
    setArchivePickIndex('');
    setArchiveFileName('');

    try {
      const { data } = await axios.post('/admin/programming/archive/resolve', {
        identifier: identifier || undefined,
        url: trimmedUrl || undefined,
      });
      const files = Array.isArray(data?.files) ? data.files : [];
      setArchiveLookup({
        loading: false,
        identifier: data?.identifier || identifier,
        title: data?.title || '',
        files,
      });
      if (files.length === 0) {
        setFeedback((prev) => ({
          ...prev,
          program: 'No MP4 files found for this item.',
        }));
      } else {
        setFeedback((prev) => ({
          ...prev,
          program: `Found ${files.length} MP4 file${files.length === 1 ? '' : 's'}. Pick one below.`,
        }));
      }
    } catch (error) {
      setArchiveLookup((prev) => ({ ...prev, loading: false, files: [] }));
      setFeedback((prev) => ({
        ...prev,
        error: error.response?.data?.message ?? 'Could not fetch Archive.org files.',
      }));
    }
  };

  const handleArchivePickIndexApply = () => {
    const index = Number(archivePickIndex);
    if (!Number.isInteger(index) || index < 1 || index > archiveLookup.files.length) {
      setFeedback((prev) => ({ ...prev, error: 'Pick a valid file number.', program: '' }));
      return;
    }
    const file = archiveLookup.files[index - 1];
    if (!file?.name) {
      setFeedback((prev) => ({ ...prev, error: 'Selected file is missing a name.', program: '' }));
      return;
    }
    setArchiveFileName(file.name);
    setFeedback((prev) => ({ ...prev, error: '', program: 'File selected. Click Build MP4.' }));
  };

  const handleArchiveBuildFromDetails = () => {
    if (!archiveIdentifier) {
      setFeedback((prev) => ({ ...prev, error: 'Add an Archive.org identifier first.', program: '' }));
      return;
    }
    const fileName = archiveFileName.trim();
    if (!fileName.toLowerCase().endsWith('.mp4')) {
      setFeedback((prev) => ({ ...prev, error: 'Archive.org file name must end with .mp4.', program: '' }));
      return;
    }
    const downloadUrl = normalizeArchiveUrl(
      `https://archive.org/download/${archiveIdentifier}/${fileName}`
    );
    setFeedback((prev) => ({ ...prev, error: '', program: '' }));
    setProgramForm((prev) => ({
      ...prev,
      media_type: 'video',
      source_url: downloadUrl,
      metadata: {
        ...(prev.metadata || {}),
        archive_url: downloadUrl,
        archive_identifier: archiveIdentifier,
        archive_file: fileName,
      },
    }));
    setFeedback((prev) => ({
      ...prev,
      program: 'Archive.org MP4 applied.',
    }));
  };

  const handleArchiveChannelFormChange = (event) => {
    const { name, type, checked, value } = event.target;
    setArchiveChannelForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleArchiveSourceFormChange = (event) => {
    const { name, value } = event.target;
    setArchiveSourceForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleArchiveAutoShuffleToggle = (checked) => {
    setArchiveChannelForm((prev) => ({
      ...prev,
      fallback_mode: checked ? 'archive_shuffle' : 'none',
    }));
  };

  const startArchiveChannelEdit = (channelId) => {
    const channel = archiveChannels.find((item) => Number(item.id) === Number(channelId));
    if (!channel) {
      setEditingArchiveChannel(null);
      setArchiveChannelForm(defaultArchiveChannelForm());
      return;
    }

    setEditingArchiveChannel(channel);
    setArchiveChannelForm({
      name: channel.name || '',
      slug: channel.slug || '',
      fallback_mode: channel.fallback_mode || 'archive_shuffle',
      is_enabled: Boolean(channel.is_enabled),
      shuffle_lookahead_count: channel.shuffle_lookahead_count ?? 10,
      shuffle_avoid_repeat_hours: channel.shuffle_avoid_repeat_hours ?? 24,
    });
    setFeedback((prev) => ({ ...prev, error: '', schedule: '' }));
  };

  const resetArchiveChannelForm = () => {
    setEditingArchiveChannel(null);
    setArchiveChannelForm(defaultArchiveChannelForm());
    setFeedback((prev) => ({ ...prev, error: '', schedule: '' }));
  };

  const applyOwnUploadsQueryPreset = () => {
    const uploader = archiveOwnUploader.trim();
    if (!uploader) {
      setFeedback((prev) => ({
        ...prev,
        error: 'Add your Archive uploader email first.',
      }));
      return;
    }

    setArchiveSourceForm((prev) => ({
      ...prev,
      name: prev.name || 'Only own uploads',
      query: `mediatype:movies AND uploader:"${uploader}" AND format:"H.264 IA"`,
    }));
    setFeedback((prev) => ({
      ...prev,
      error: '',
      schedule: 'Applied own-uploads preset query.',
    }));
  };

  const resetArchiveSourceForm = useCallback(() => {
    setArchiveSourceForm((prev) => ({
      ...defaultArchiveSourceForm(),
      channel_id: prev.channel_id || archivePreviewForm.channel_id || '',
    }));
    setEditingArchiveSource(null);
  }, [archivePreviewForm.channel_id]);

  const startArchiveSourceEdit = (source) => {
    setEditingArchiveSource(source);
    setArchiveSourceForm({
      channel_id: source?.channel_id ? String(source.channel_id) : '',
      name: source?.name || '',
      query: source?.query || '',
      weight: source?.weight ?? 1,
    });
    setFeedback((prev) => ({ ...prev, error: '', schedule: '' }));
  };

  const handleArchivePreviewFormChange = (event) => {
    const { name, type, checked, value } = event.target;
    setArchivePreviewForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const submitArchiveChannel = async (event) => {
    event.preventDefault();
    try {
      const payload = {
        name: archiveChannelForm.name,
        slug: archiveChannelForm.slug,
        fallback_mode: archiveChannelForm.fallback_mode,
        is_enabled: Boolean(archiveChannelForm.is_enabled),
        shuffle_lookahead_count: Number(archiveChannelForm.shuffle_lookahead_count) || 10,
        shuffle_avoid_repeat_hours: Number(archiveChannelForm.shuffle_avoid_repeat_hours) || 24,
      };

      if (editingArchiveChannel?.id) {
        await axios.put(`/admin/programming/archive/channels/${editingArchiveChannel.id}`, payload);
      } else {
        await axios.post('/admin/programming/archive/channels', payload);
      }

      setFeedback((prev) => ({
        ...prev,
        schedule: editingArchiveChannel?.id ? 'Archive channel updated.' : 'Archive channel saved.',
        error: '',
      }));
      setEditingArchiveChannel(null);
      setArchiveChannelForm(defaultArchiveChannelForm());
      await loadArchiveConfig();
    } catch (error) {
      setFeedback((prev) => ({
        ...prev,
        error: formatApiError(error, 'Could not save archive channel.'),
      }));
    }
  };

  const submitArchiveSource = async (event) => {
    event.preventDefault();
    try {
      const payload = {
        channel_id: Number(archiveSourceForm.channel_id),
        name: archiveSourceForm.name,
        query: archiveSourceForm.query,
        weight: Number(archiveSourceForm.weight) || 1,
      };

      if (editingArchiveSource?.id) {
        await axios.put(`/admin/programming/archive/sources/${editingArchiveSource.id}`, payload);
      } else {
        await axios.post('/admin/programming/archive/sources', payload);
      }

      resetArchiveSourceForm();
      setFeedback((prev) => ({
        ...prev,
        schedule: editingArchiveSource?.id ? 'Archive source updated.' : 'Archive source saved.',
        error: '',
      }));
      await loadArchiveConfig();
    } catch (error) {
      setFeedback((prev) => ({
        ...prev,
        error: formatApiError(error, 'Could not save archive source.'),
      }));
    }
  };

  const handleToggleArchiveSource = async (sourceId) => {
    try {
      await axios.post(`/admin/programming/archive/sources/${sourceId}/toggle`);
      await loadArchiveConfig();
    } catch (error) {
      setFeedback((prev) => ({
        ...prev,
        error: formatApiError(error, 'Could not toggle archive source.'),
      }));
    }
  };

  const handleDeleteArchiveSource = async (sourceId) => {
    if (!window.confirm('Delete this archive source?')) return;

    try {
      await axios.delete(`/admin/programming/archive/sources/${sourceId}`);
      if (editingArchiveSource?.id === sourceId) {
        resetArchiveSourceForm();
      }
      setSelectedArchiveSourceIds((prev) => prev.filter((id) => id !== sourceId));
      setFeedback((prev) => ({ ...prev, schedule: 'Archive source deleted.', error: '' }));
      await loadArchiveConfig();
    } catch (error) {
      setFeedback((prev) => ({
        ...prev,
        error: formatApiError(error, 'Could not delete archive source.'),
      }));
    }
  };

  const handleToggleArchiveSourceSelection = (sourceId, checked) => {
    setSelectedArchiveSourceIds((prev) => {
      if (checked) {
        if (prev.includes(sourceId)) return prev;
        return [...prev, sourceId];
      }
      return prev.filter((id) => id !== sourceId);
    });
  };

  const handleSelectAllArchiveSources = (checked) => {
    if (!checked) {
      setSelectedArchiveSourceIds([]);
      return;
    }
    setSelectedArchiveSourceIds(archiveSources.map((source) => source.id));
  };

  const handleDeleteSelectedArchiveSources = async () => {
    if (selectedArchiveSourceIds.length === 0) return;

    const idsToDelete = [...selectedArchiveSourceIds];
    const confirmed = window.confirm(
      `Delete ${idsToDelete.length} selected archive source${idsToDelete.length === 1 ? '' : 's'}?`
    );
    if (!confirmed) return;

    setBulkDeletingArchiveSources(true);
    try {
      const results = await Promise.allSettled(
        idsToDelete.map((sourceId) => axios.delete(`/admin/programming/archive/sources/${sourceId}`))
      );

      const deletedCount = results.filter((result) => result.status === 'fulfilled').length;
      const failedResults = results.filter((result) => result.status === 'rejected');

      if (editingArchiveSource?.id && idsToDelete.includes(editingArchiveSource.id)) {
        resetArchiveSourceForm();
      }

      if (failedResults.length === 0) {
        setFeedback((prev) => ({
          ...prev,
          schedule: `Deleted ${deletedCount} archive source${deletedCount === 1 ? '' : 's'}.`,
          error: '',
        }));
      } else {
        const firstError = failedResults[0]?.reason;
        setFeedback((prev) => ({
          ...prev,
          schedule: deletedCount > 0 ? `Deleted ${deletedCount} archive source${deletedCount === 1 ? '' : 's'}.` : prev.schedule,
          error: `Failed to delete ${failedResults.length} source${failedResults.length === 1 ? '' : 's'}: ${formatApiError(firstError, 'Please retry.')}`,
        }));
      }

      await loadArchiveConfig();
    } finally {
      setBulkDeletingArchiveSources(false);
    }
  };

  const handleToggleArchivePreviewItemSelection = (itemKey, checked) => {
    setSelectedArchivePreviewItemKeys((prev) => {
      if (checked) {
        if (prev.includes(itemKey)) return prev;
        return [...prev, itemKey];
      }
      return prev.filter((key) => key !== itemKey);
    });
  };

  const handleSelectAllArchivePreviewItems = (checked) => {
    if (!checked) {
      setSelectedArchivePreviewItemKeys([]);
      return;
    }
    setSelectedArchivePreviewItemKeys(
      archivePreview.items.map((item, index) => buildArchivePreviewItemKey(item, index))
    );
  };

  const ensureArchivePreviewProgram = async (item, knownPrograms = libraryPrograms) => {
    if (!item?.source_url) {
      throw new Error('Selected preview item has no playable URL.');
    }

    const title = item.title || item.identifier || 'Archive Fallback';
    const durationSeconds = Number(item.duration_seconds) > 0 ? Number(item.duration_seconds) : 900;
    const existing = knownPrograms.find((program) => {
      return (
        String(program?.source_url || '') === String(item.source_url) &&
        String(program?.media_type || '') === 'video'
      );
    });

    if (existing?.id) {
      return {
        programId: existing.id,
        programTitle: existing.title || title,
        durationSeconds,
        createdProgram: null,
      };
    }

    const payload = {
      title: `[Archive] ${title}`.slice(0, 255),
      category: 'other',
      media_type: 'video',
      source_url: item.source_url,
      duration_seconds: durationSeconds,
      usage: 'primary',
      metadata: {
        archive_identifier: item.identifier || null,
        archive_file: item.file_name || null,
        archive_license: item.license || null,
        archive_source_id: item.source_id || null,
        archive_channel_id: item.channel_id || null,
      },
    };

    const { data } = await axios.post('/admin/programming/programs', payload);
    const createdProgram = data?.program;
    if (!createdProgram?.id) {
      throw new Error('Could not resolve a program for this preview item.');
    }

    return {
      programId: createdProgram.id,
      programTitle: createdProgram.title || payload.title,
      durationSeconds,
      createdProgram,
    };
  };

  const submitArchiveQueuePreview = async (event) => {
    event.preventDefault();
    setArchivePreview((prev) => ({ ...prev, loading: true }));

    try {
      const payload = {
        channel_id: Number(archivePreviewForm.channel_id),
        count: Number(archivePreviewForm.count) || 10,
        seed: archivePreviewForm.seed || undefined,
        debug: Boolean(archivePreviewForm.debug),
        exclude_recent: Boolean(archivePreviewForm.exclude_recent),
        min_duration_seconds: archivePreviewForm.min_duration_seconds
          ? Number(archivePreviewForm.min_duration_seconds)
          : undefined,
        max_duration_seconds: archivePreviewForm.max_duration_seconds
          ? Number(archivePreviewForm.max_duration_seconds)
          : undefined,
        license_allowlist: archivePreviewForm.license_allowlist
          ? archivePreviewForm.license_allowlist
              .split(',')
              .map((item) => item.trim())
              .filter(Boolean)
          : undefined,
      };

      const { data } = await axios.post('/admin/programming/archive/queue/preview', payload);
      setArchivePreview({
        loading: false,
        items: Array.isArray(data?.items) ? data.items : [],
        meta: data,
      });
      setSelectedArchivePreviewItemKeys([]);
      setArchiveQueueConfirmation({
        open: false,
        afterLastScheduled: false,
        startAt: null,
        items: [],
      });
      setFeedback((prev) => ({ ...prev, schedule: 'Queue preview generated.', error: '' }));
    } catch (error) {
      setArchivePreview((prev) => ({ ...prev, loading: false }));
      setFeedback((prev) => ({
        ...prev,
        error: formatApiError(error, 'Could not preview archive queue.'),
      }));
    }
  };

  const resolveQueueStartAfterLastScheduledSlot = async () => {
    const now = new Date();
    const startWindow = new Date(now.getTime() - 60 * 60 * 1000);
    const endWindow = new Date(now.getTime() + 365 * 24 * 60 * 60 * 1000);

    const { data } = await axios.get('/admin/programming/schedule/timeline', {
      params: {
        start: startWindow.toISOString(),
        end: endWindow.toISOString(),
      },
    });

    const items = Array.isArray(data?.items) ? data.items : [];
    const latestEnd = items.reduce((latest, slot) => {
      const endAt = new Date(slot?.ends_at);
      if (Number.isNaN(endAt.getTime())) return latest;
      if (!latest || endAt > latest) return endAt;
      return latest;
    }, null);

    if (!latestEnd) {
      return null;
    }

    // Avoid inclusive boundary overlap checks on the backend.
    return new Date(latestEnd.getTime() + 1000);
  };

  const getSelectedArchivePreviewItems = () =>
    archivePreview.items
      .map((item, index) => ({ item, index, key: buildArchivePreviewItemKey(item, index) }))
      .filter(({ key }) => selectedArchivePreviewItemKeys.includes(key));

  const queueSelectedArchivePreviewToTimeline = async ({ afterLastScheduled = false } = {}) => {
    if (selectedArchivePreviewItemKeys.length === 0) {
      setFeedback((prev) => ({ ...prev, error: 'Select at least one preview item to queue.' }));
      return;
    }

    const selectedItems = getSelectedArchivePreviewItems();

    if (selectedItems.length === 0) {
      setFeedback((prev) => ({ ...prev, error: 'No valid preview items are selected.' }));
      return;
    }

    let startAt = null;
    if (afterLastScheduled) {
      startAt = await resolveQueueStartAfterLastScheduledSlot();
      if (startAt) {
        setArchiveQueueStartAt(toDateTimeLocal(startAt));
      }
    }
    if (!startAt) {
      startAt = new Date(archiveQueueStartAt);
    }
    if (Number.isNaN(startAt.getTime())) {
      setFeedback((prev) => ({ ...prev, error: 'Choose a valid queue start time.' }));
      return;
    }

    let cursor = new Date(startAt);
    const planItems = selectedItems.map(({ item }) => {
      const durationSeconds = Number(item.duration_seconds) > 0 ? Number(item.duration_seconds) : 900;
      const startsAt = new Date(cursor);
      const endsAt = new Date(startsAt.getTime() + durationSeconds * 1000);
      cursor = endsAt;
      return {
        item,
        durationSeconds,
        startsAt,
        endsAt,
      };
    });

    setArchiveQueueConfirmation({
      open: true,
      afterLastScheduled,
      startAt,
      items: planItems,
    });
    setFeedback((prev) => ({
      ...prev,
      error: '',
      schedule: 'Review the queue plan and confirm to create schedule slots.',
    }));
  };

  const closeArchiveQueueConfirmation = () => {
    setArchiveQueueConfirmation((prev) => ({ ...prev, open: false }));
  };

  const confirmArchiveQueueToTimeline = async () => {
    const confirmation = archiveQueueConfirmation;
    if (!confirmation.open || confirmation.items.length === 0) {
      return;
    }

    setQueueingArchivePreview(true);
    setSaving((prev) => ({ ...prev, schedule: true }));
    setFeedback((prev) => ({ ...prev, schedule: '', error: '' }));

    try {
      let created = 0;
      const knownPrograms = [...libraryPrograms];

      for (const planned of confirmation.items) {
        const resolved = await ensureArchivePreviewProgram(planned.item, knownPrograms);
        await axios.post('/admin/programming/schedule', {
          type: 'program',
          title: resolved.programTitle,
          program_id: resolved.programId,
          starts_at: planned.startsAt.toISOString(),
          ends_at: planned.endsAt.toISOString(),
          obs_scene: null,
          obs_profile: null,
          payload: {
            auto_filler: {
              enabled: true,
            },
          },
        });

        if (resolved.createdProgram?.id) {
          knownPrograms.push(resolved.createdProgram);
        }

        created += 1;
      }

      const lastEnd = confirmation.items[confirmation.items.length - 1]?.endsAt;
      setSelectedArchivePreviewItemKeys([]);
      if (lastEnd) {
        setArchiveQueueStartAt(toDateTimeLocal(new Date(lastEnd)));
      }
      setArchiveQueueConfirmation({
        open: false,
        afterLastScheduled: false,
        startAt: null,
        items: [],
      });
      setFeedback((prev) => ({
        ...prev,
        error: '',
        schedule: confirmation.afterLastScheduled
          ? `Queued ${created} preview item${created === 1 ? '' : 's'} after last scheduled slot.`
          : `Queued ${created} preview item${created === 1 ? '' : 's'} to timeline.`,
      }));

      await Promise.all([loadSchedule(), loadProgramOptions(), loadPrograms()]);
    } catch (error) {
      setFeedback((prev) => ({
        ...prev,
        error: formatApiError(error, 'Could not queue selected preview items.'),
      }));
    } finally {
      setQueueingArchivePreview(false);
      setSaving((prev) => ({ ...prev, schedule: false }));
    }
  };

  const applyArchivePreviewAsSchedulePreset = async (item) => {
    try {
      const { programId, programTitle, durationSeconds } = await ensureArchivePreviewProgram(item);

      const start = new Date();
      start.setMinutes(start.getMinutes() + 5);
      start.setSeconds(0, 0);
      const end = new Date(start.getTime() + durationSeconds * 1000);

      setScheduleForm((prev) => ({
        ...prev,
        type: 'program',
        title: programTitle,
        program_id: String(programId),
        starts_at: toDateTimeLocal(start),
        ends_at: toDateTimeLocal(end),
      }));

      setFeedback((prev) => ({
        ...prev,
        error: '',
        schedule: 'Schedule preset applied from queue preview. Review times and click Schedule slot.',
      }));

      await Promise.all([loadProgramOptions(), loadPrograms()]);

      if (typeof window !== 'undefined') {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } catch (error) {
      setFeedback((prev) => ({
        ...prev,
        error: error.response?.data?.message ?? 'Could not apply queue preview as schedule preset.',
      }));
    }
  };

  const startProgramEdit = (program) => {
    setEditingProgram(program);
    setFeedback((prev) => ({ ...prev, program: '', error: '' }));
    setProgramForm({
      title: program.title || '',
      description: program.description || '',
      category: program.category || 'other',
      media_type: program.media_type || 'video',
      source_url: program.source_url || '',
      thumbnail_url: program.thumbnail_url || '',
      duration_seconds: program.duration_seconds ? String(program.duration_seconds) : '',
      duration_hms: Number.isFinite(program.duration_seconds) ? formatDuration(program.duration_seconds) : '',
      metadata: program.metadata || {},
      usage: program.usage || 'primary',
      filler_tags: Array.isArray(program.filler_tags)
        ? program.filler_tags.join(', ')
        : program.filler_tags || '',
      is_loopable: Boolean(program.is_loopable),
    });
    setOdyseeUrl(program.metadata?.odysee_url || '');
    setYoutubeUrl(program.metadata?.youtube_url || '');
    const storedArchiveUrl = program.metadata?.archive_url || '';
    setArchiveUrl(storedArchiveUrl);
    setArchiveLookup({ loading: false, identifier: '', title: '', files: [] });
    setArchivePickIndex('');
    if (storedArchiveUrl) {
      const archiveResult = parseArchiveMp4Url(storedArchiveUrl);
      if (archiveResult.kind === 'details') {
        setArchiveIdentifier(archiveResult.identifier);
      } else {
        setArchiveIdentifier(program.metadata?.archive_identifier || '');
      }
    } else {
      setArchiveIdentifier(program.metadata?.archive_identifier || '');
    }
    setArchiveFileName(program.metadata?.archive_file || '');
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleScheduleChange = (event) => {
    const { name, value, type, checked } = event.target;
    const nextValue = type === 'checkbox' ? checked : value;
    setScheduleForm((prev) => {
      const resolveAutoEndAt = (startsAtValue, programIdValue) => {
        if (!startsAtValue || !programIdValue) return null;
        const startDate = new Date(startsAtValue);
        if (Number.isNaN(startDate.getTime())) return null;

        const selectedScheduleProgram = programOptions.find(
          (program) => String(program.id) === String(programIdValue)
        );
        const durationSeconds = Number(selectedScheduleProgram?.duration_seconds);
        if (!Number.isFinite(durationSeconds) || durationSeconds <= 0) return null;

        return toDateTimeLocal(new Date(startDate.getTime() + durationSeconds * 1000));
      };

      if (name === 'type') {
        const nextState = {
          ...prev,
          type: nextValue,
          program_id: '',
          live_input_url: '',
          youtube_url: '',
          jitsi_room: '',
          jitsi_join_url: '',
        };
        if (!editingSchedule && nextValue === 'program') {
          const computedEnd = resolveAutoEndAt(nextState.starts_at, nextState.program_id);
          if (computedEnd) nextState.ends_at = computedEnd;
        }
        return nextState;
      }

      if (name === 'recurrence_type' && nextValue === 'none') {
        return {
          ...prev,
          recurrence_type: nextValue,
          recurrence_count: 1,
        };
      }

      const nextState = { ...prev, [name]: nextValue };
      if (!editingSchedule && nextState.type === 'program' && (name === 'starts_at' || name === 'program_id')) {
        const computedEnd = resolveAutoEndAt(nextState.starts_at, nextState.program_id);
        if (computedEnd) {
          nextState.ends_at = computedEnd;
        }
      }

      return nextState;
    });
  };

  const startScheduleEdit = (slot) => {
    setEditingSchedule(slot);
    setFeedback((prev) => ({ ...prev, schedule: '', error: '' }));
    const autoFiller = slot.payload?.auto_filler ?? {};
    const autoFillerTags = Array.isArray(autoFiller.tags)
      ? autoFiller.tags.join(', ')
      : autoFiller.tags || '';
    setScheduleForm({
      type: slot.type || 'program',
      title: slot.title || '',
      program_id: slot.program_id || '',
      live_input_url: slot.live_input_url || '',
      youtube_url: slot.youtube_url || '',
      jitsi_room: slot.jitsi_room || '',
      jitsi_join_url: slot.jitsi_join_url || '',
      starts_at: toDateTimeLocal(new Date(slot.starts_at)),
      ends_at: toDateTimeLocal(new Date(slot.ends_at)),
      obs_scene: slot.obs_scene || '',
      obs_profile: slot.obs_profile || '',
      recurrence_type: slot.recurrence_type || 'none',
      recurrence_count: slot.recurrence_sequence || slot.recurrence_count || 1,
      auto_filler_enabled: autoFiller.enabled !== false,
      auto_filler_category: autoFiller.category || 'any',
      auto_filler_tags: autoFillerTags,
    });
    setViewMode('list');
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const openManualStart = (target) => {
    setManualStart((prev) => ({
      ...prev,
      target,
      starts_at: toDateTimeLocal(new Date()),
      immediate: false,
      force: false,
      loading: false,
      error: '',
      current: null,
    }));
  };

  const closeManualStart = () => {
    setManualStart((prev) => ({ ...prev, target: null, error: '', loading: false }));
  };

  const handleManualStartChange = (event) => {
    const { name, value, type, checked } = event.target;
    setManualStart((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const submitManualStart = async (event) => {
    event.preventDefault();
    if (!manualStart.target) return;

    setManualStart((prev) => ({ ...prev, loading: true, error: '' }));

    try {
      const payload = {
        starts_at: manualStart.starts_at,
        immediate: manualStart.immediate,
        force: manualStart.force,
      };

      if (!manualStart.immediate) {
        payload.starts_at = toIsoString(manualStart.starts_at);
      }

      if (manualStart.target.type === 'schedule') {
        await axios.post(`/admin/programming/schedule/${manualStart.target.id}/start`, payload);
      } else {
        await axios.post(`/admin/programming/programs/${manualStart.target.id}/start`, payload);
      }

      await loadSchedule();
      closeManualStart();
    } catch (error) {
      if (error.response?.status === 409 && error.response?.data?.current) {
        setManualStart((prev) => ({
          ...prev,
          current: error.response.data.current,
          error: error.response.data.message ?? 'A program is already playing. Confirm override to continue.',
          loading: false,
        }));
        return;
      }
      setManualStart((prev) => ({
        ...prev,
        error: error.response?.data?.message ?? 'Unable to start program.',
      }));
    } finally {
      setManualStart((prev) => ({ ...prev, loading: false }));
    }
  };

  const submitProgram = async (event) => {
    event.preventDefault();
    const trimmedTitle = programForm.title.trim();
    const hasDuplicateTitle = libraryPrograms.some((program) => {
      if (!program?.title) return false;
      if (editingProgram && program.id === editingProgram.id) return false;
      return program.title === trimmedTitle;
    });

    if (hasDuplicateTitle) {
      setFeedback((prev) => ({
        ...prev,
        program: '',
        error: 'Duplicate program name. Please choose a different title.',
      }));
      return;
    }

    if (programForm.duration_hms) {
      const parsedDuration = parseDurationHms(programForm.duration_hms);
      if (parsedDuration == null) {
        setFeedback((prev) => ({
          ...prev,
          program: '',
          error: 'Duration must be in HH:MM:SS format.',
        }));
        return;
      }
    }

    setSaving((prev) => ({ ...prev, program: true }));
    setFeedback((prev) => ({ ...prev, program: '', error: '' }));

    const isEditing = Boolean(editingProgram);

    try {
      const resolvedDurationSeconds = programForm.duration_hms
        ? parseDurationHms(programForm.duration_hms)
        : programForm.duration_seconds
          ? Number(programForm.duration_seconds)
          : null;

      const { duration_hms, ...programPayload } = programForm;
      const payload = {
        ...programPayload,
        duration_seconds: resolvedDurationSeconds,
        filler_tags: programForm.filler_tags
          ? programForm.filler_tags
              .split(',')
              .map((tag) => tag.trim())
              .filter(Boolean)
          : null,
        metadata: programForm.metadata || null,
      };

      payload.is_loopable = Boolean(programForm.is_loopable);

      if (isEditing) {
        await axios.put(`/admin/programming/programs/${editingProgram.id}`, payload);
        setFeedback((prev) => ({ ...prev, program: 'Program updated.' }));
      } else {
        await axios.post('/admin/programming/programs', payload);
        setFeedback((prev) => ({ ...prev, program: 'Program saved.' }));
      }

      resetProgramForm();
      if (!isEditing) {
        setProgramPagination((prev) => ({ ...prev, page: 1 }));
      }
      await Promise.all([
        loadProgramOptions(),
        loadPrograms({ page: isEditing ? programPagination.page : 1 }),
      ]);
    } catch (error) {
      setFeedback((prev) => ({
        ...prev,
        error: error.response?.data?.message ?? 'Could not save program.',
      }));
    } finally {
      setSaving((prev) => ({ ...prev, program: false }));
    }
  };

  const submitSchedule = async (event) => {
    event.preventDefault();
    setSaving((prev) => ({ ...prev, schedule: true }));
    setFeedback((prev) => ({ ...prev, schedule: '', error: '' }));

    const isEditing = Boolean(editingSchedule);

    const payload = isEditing && editingSchedule?.status === 'in_progress'
      ? {
          ends_at: toIsoString(scheduleForm.ends_at),
        }
      : {
          type: scheduleForm.type,
          title: scheduleForm.title,
          starts_at: toIsoString(scheduleForm.starts_at),
          ends_at: toIsoString(scheduleForm.ends_at),
          obs_scene: scheduleForm.obs_scene || null,
          obs_profile: scheduleForm.obs_profile || null,
          ...(scheduleForm.type === 'program'
            ? { program_id: scheduleForm.program_id }
            : {
                live_input_url: scheduleForm.live_input_url,
                youtube_url: scheduleForm.youtube_url || null,
                jitsi_room: scheduleForm.jitsi_room || null,
                jitsi_join_url: scheduleForm.jitsi_join_url || null,
              }),
        };

    if (scheduleForm.type === 'program' && !(isEditing && editingSchedule?.status === 'in_progress')) {
      const tagList = (scheduleForm.auto_filler_tags ?? '')
        .split(',')
        .map((tag) => tag.trim())
        .filter(Boolean);
      const autoFillerCategory =
        scheduleForm.auto_filler_category && scheduleForm.auto_filler_category !== 'any'
          ? scheduleForm.auto_filler_category
          : null;

      payload.payload = {
        ...(payload.payload || {}),
        auto_filler: {
          enabled: Boolean(scheduleForm.auto_filler_enabled),
          ...(autoFillerCategory ? { category: autoFillerCategory } : {}),
          ...(tagList.length ? { tags: tagList } : {}),
        },
      };
    }

    if (!isEditing && scheduleForm.recurrence_type !== 'none') {
      payload.recurrence = {
        type: scheduleForm.recurrence_type,
        count: Number(scheduleForm.recurrence_count) || 1,
      };
    }

    try {
      if (isEditing) {
        await axios.put(`/admin/programming/schedule/${editingSchedule.id}`, payload);
        setFeedback((prev) => ({ ...prev, schedule: 'Slot updated.' }));
      } else {
        await axios.post('/admin/programming/schedule', payload);
        setFeedback((prev) => ({ ...prev, schedule: 'Slot scheduled.' }));
      }
      resetScheduleForm();
      await loadSchedule();
    } catch (error) {
      setFeedback((prev) => ({
        ...prev,
        error: error.response?.data?.message ?? 'Could not schedule slot.',
      }));
    } finally {
      setSaving((prev) => ({ ...prev, schedule: false }));
    }
  };

  const handleCancelSlot = async (slotId) => {
    if (!window.confirm('Delete this slot? This will cancel the scheduled window.')) return;

    try {
      await axios.post(`/admin/programming/schedule/${slotId}/cancel`);
      await loadSchedule();
    } catch (error) {
      setFeedback((prev) => ({
        ...prev,
        error: error.response?.data?.message ?? 'Unable to delete slot.',
      }));
    }
  };

  const handleDeleteProgram = async (programId) => {
    if (!window.confirm('Delete this program?')) return;

    try {
      await axios.delete(`/admin/programming/programs/${programId}`);
      await Promise.all([loadProgramOptions(), loadPrograms()]);
    } catch (error) {
      setFeedback((prev) => ({
        ...prev,
        error: error.response?.data?.message ?? 'Unable to delete program.',
      }));
    }
  };

  const handleProgramSearch = (event) => {
    const { value } = event.target;
    setProgramFilters((prev) => ({ ...prev, search: value }));
  };

  const handleProgramSort = (event) => {
    const { value } = event.target;
    setProgramFilters((prev) => ({ ...prev, sort: value }));
  };

  const handleCalendarShift = (weeks) => {
    const newRange = getCalendarRange(addDays(calendarRange.start, weeks * 7));
    setCalendarRange(newRange);
  };

  const calendarDays = useMemo(() => buildCalendarDays(calendarRange), [calendarRange]);

  const eventsByDay = useMemo(() => {
    const map = {};
    scheduleItems.forEach((slot) => {
      const dateKey = new Date(slot.starts_at).toISOString().slice(0, 10);
      map[dateKey] = map[dateKey] || [];
      map[dateKey].push(slot);
    });
    Object.values(map).forEach((events) =>
      events.sort((a, b) => new Date(a.starts_at) - new Date(b.starts_at))
    );
    return map;
  }, [scheduleItems]);

  const statusBadge = (status) => {
    const meta = scheduleStatusMap[status] ?? scheduleStatusMap.pending;
    return <span className={`badge ${meta.tone}`}>{meta.label}</span>;
  };

  const sortedPrograms = useMemo(
    () => [...programOptions].sort((a, b) => (a.title ?? '').localeCompare(b.title ?? '')),
    [programOptions]
  );
  const buildProgramPreview = useCallback((program) => {
    if (!program) return null;
    const mediaUrl = program.source_url || '';
    const odyseeEmbed = toOdyseeEmbed(mediaUrl);
    const isVideo = program.media_type === 'video' && mediaUrl && !odyseeEmbed;
    const isImage = program.media_type === 'image' && mediaUrl;
    const isEmbeddable =
      (mediaUrl && ['web', 'playlist'].includes(program.media_type ?? '')) || Boolean(odyseeEmbed);

    return {
      mediaUrl,
      odyseeEmbed,
      isVideo,
      isImage,
      isEmbeddable,
    };
  }, []);
  const selectedProgram = useMemo(
    () => programOptions.find((program) => String(program.id) === String(scheduleForm.program_id)),
    [programOptions, scheduleForm.program_id]
  );
  const selectedProgramPreview = useMemo(() => {
    return buildProgramPreview(selectedProgram);
  }, [buildProgramPreview, selectedProgram]);
  const programFormPreview = useMemo(() => {
    return buildProgramPreview({
      title: programForm.title,
      media_type: programForm.media_type,
      source_url: programForm.source_url,
      thumbnail_url: programForm.thumbnail_url,
    });
  }, [buildProgramPreview, programForm.media_type, programForm.source_url, programForm.thumbnail_url, programForm.title]);
  const visiblePrograms = useMemo(() => {
    const trimmedSearch = programFilters.search.trim().toLowerCase();
    const compareTitle = (a, b) =>
      (a.title ?? '').localeCompare(b.title ?? '', undefined, { sensitivity: 'base' });
    const compareUploaded = (a, b) => {
      const aTime = new Date(a.created_at ?? 0).getTime();
      const bTime = new Date(b.created_at ?? 0).getTime();
      return aTime - bTime;
    };
    let items = [...libraryPrograms];

    if (trimmedSearch) {
      items = items.filter((program) => {
        const haystack = `${program.title ?? ''} ${program.description ?? ''}`.toLowerCase();
        return haystack.includes(trimmedSearch);
      });
    }

    switch (programFilters.sort) {
      case 'title-desc':
        items.sort((a, b) => compareTitle(b, a));
        break;
      case 'uploaded-asc':
        items.sort((a, b) => compareUploaded(a, b));
        break;
      case 'uploaded-desc':
        items.sort((a, b) => compareUploaded(b, a));
        break;
      default:
        items.sort(compareTitle);
    }

    return items;
  }, [libraryPrograms, programFilters.search, programFilters.sort]);
  const outOfSpecPrograms = useMemo(
    () =>
      sortedPrograms.filter((program) => {
        return Boolean(
          getDurationVariance(program.duration_seconds, targetDurationSeconds, durationToleranceSeconds)
        );
      }),
    [sortedPrograms, targetDurationSeconds, durationToleranceSeconds]
  );
  const outOfSpecSummary = useMemo(() => {
    return outOfSpecPrograms.reduce(
      (acc, program) => {
        const variance = getDurationVariance(
          program.duration_seconds,
          targetDurationSeconds,
          durationToleranceSeconds
        );
        if (!variance) return acc;
        if (variance.delta < 0) acc.short += 1;
        else acc.long += 1;
        return acc;
      },
      { short: 0, long: 0 }
    );
  }, [outOfSpecPrograms, targetDurationSeconds, durationToleranceSeconds]);

  const playbackConflict = useMemo(() => {
    if (!manualStart.current || !manualStart.target) return false;
    return manualStart.current.id !== manualStart.target.id;
  }, [manualStart]);

  const hasProgramSearch = programFilters.search.trim().length > 0;
  const totalProgramCount = programPagination.total || libraryPrograms.length;
  const programCountLabel = hasProgramSearch
    ? `Showing ${visiblePrograms.length} of ${totalProgramCount} (filtered)`
    : `Showing ${programPagination.from || 0}-${programPagination.to || 0} of ${totalProgramCount}`;
  const isLibraryPage = defaultTab === 'library';

  if (!isSuperAdmin()) {
    return (
      <div className="card">
        <h2>Programming workspace</h2>
        <p className="text-muted">Only Super Admins can manage the TV scheduler.</p>
      </div>
    );
  }

  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h2 style={{ fontSize: '1.5rem' }}>TV Programming</h2>
            <p className="text-muted">Schedule prerecorded programs or live windows that sync with OBS.</p>
          </div>
          {!isLibraryPage && (
            <div className="view-toggle">
              <button type="button" className={`btn btn-muted ${viewMode === 'list' ? 'active' : ''}`} onClick={() => setViewMode('list')}>
                List
              </button>
              <button type="button" className={`btn btn-muted ${viewMode === 'calendar' ? 'active' : ''}`} onClick={() => setViewMode('calendar')}>
                Calendar
              </button>
            </div>
          )}
        </div>
        {feedback.error && <div className="error-banner">{feedback.error}</div>}
      </div>

      {!isLibraryPage && (
      <div className="card" style={{ display: 'grid', gap: '1rem' }}>
        <div>
          <h3 style={{ marginBottom: '0.35rem' }}>Archive Shuffle Control</h3>
          <p className="text-muted">Manage fallback channels/sources and preview shuffled candidates before airtime.</p>
        </div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '1rem',
          }}
        >
          <form onSubmit={submitArchiveChannel} className="card" style={{ boxShadow: 'var(--shadow-sm)' }}>
            <h4 style={{ marginBottom: '0.6rem' }}>
              {editingArchiveChannel ? 'Edit channel' : 'Create channel'}
            </h4>
            {archiveChannels.length > 0 && (
              <div className="field">
                <label htmlFor="archive_channel_edit_select">Existing channel</label>
                <select
                  id="archive_channel_edit_select"
                  className="input"
                  value={editingArchiveChannel?.id || ''}
                  onChange={(event) => startArchiveChannelEdit(event.target.value)}
                >
                  <option value="">Create new channel…</option>
                  {archiveChannels.map((channel) => (
                    <option key={channel.id} value={channel.id}>
                      {channel.name} ({channel.slug})
                    </option>
                  ))}
                </select>
              </div>
            )}
            {editingArchiveChannel && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '0.5rem',
                  marginBottom: '0.65rem',
                  padding: '0.5rem 0.65rem',
                  borderRadius: '8px',
                  background: 'rgba(15, 23, 42, 0.05)',
                }}
              >
                <span style={{ fontSize: '0.9rem' }}>
                  Editing “{editingArchiveChannel.name}”
                </span>
                <button type="button" className="btn btn-muted" onClick={resetArchiveChannelForm}>
                  Cancel edit
                </button>
              </div>
            )}
            <div className="field">
              <label htmlFor="archive_channel_name">Name</label>
              <input id="archive_channel_name" name="name" className="input" value={archiveChannelForm.name} onChange={handleArchiveChannelFormChange} required />
            </div>
            <div className="field">
              <label htmlFor="archive_channel_slug">Slug</label>
              <input id="archive_channel_slug" name="slug" className="input" value={archiveChannelForm.slug} onChange={handleArchiveChannelFormChange} required />
            </div>
            <div className="field">
              <label htmlFor="archive_fallback_mode">Fallback mode</label>
              <select id="archive_fallback_mode" name="fallback_mode" className="input" value={archiveChannelForm.fallback_mode} onChange={handleArchiveChannelFormChange}>
                <option value="archive_shuffle">Archive shuffle</option>
                <option value="filler_only">Filler only</option>
                <option value="none">None</option>
              </select>
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <input
                type="checkbox"
                checked={archiveChannelForm.fallback_mode === 'archive_shuffle'}
                onChange={(event) => handleArchiveAutoShuffleToggle(event.target.checked)}
              />
              <span>Auto shuffle when no scheduled program is live</span>
            </label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.6rem' }}>
              <div className="field">
                <label htmlFor="archive_lookahead">Lookahead</label>
                <input id="archive_lookahead" name="shuffle_lookahead_count" className="input" type="number" min="1" max="200" value={archiveChannelForm.shuffle_lookahead_count} onChange={handleArchiveChannelFormChange} />
              </div>
              <div className="field">
                <label htmlFor="archive_repeat_hours">No-repeat hrs</label>
                <input id="archive_repeat_hours" name="shuffle_avoid_repeat_hours" className="input" type="number" min="1" max="720" value={archiveChannelForm.shuffle_avoid_repeat_hours} onChange={handleArchiveChannelFormChange} />
              </div>
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <input type="checkbox" name="is_enabled" checked={archiveChannelForm.is_enabled} onChange={handleArchiveChannelFormChange} />
              <span>Enabled</span>
            </label>
            <button type="submit" className="btn btn-primary">
              {editingArchiveChannel ? 'Update channel' : 'Save channel'}
            </button>
          </form>

          <form onSubmit={submitArchiveSource} className="card" style={{ boxShadow: 'var(--shadow-sm)' }}>
            <h4 style={{ marginBottom: '0.6rem' }}>
              {editingArchiveSource ? 'Edit source' : 'Create source'}
            </h4>
            {editingArchiveSource && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '0.5rem',
                  marginBottom: '0.65rem',
                  padding: '0.5rem 0.65rem',
                  borderRadius: '8px',
                  background: 'rgba(15, 23, 42, 0.05)',
                }}
              >
                <span style={{ fontSize: '0.9rem' }}>
                  Editing “{editingArchiveSource.name}”
                </span>
                <button type="button" className="btn btn-muted" onClick={resetArchiveSourceForm}>
                  Cancel edit
                </button>
              </div>
            )}
            <div className="field">
              <label htmlFor="archive_source_channel">Channel</label>
              <select id="archive_source_channel" name="channel_id" className="input" value={archiveSourceForm.channel_id} onChange={handleArchiveSourceFormChange} required>
                <option value="">Select channel…</option>
                {archiveChannels.map((channel) => (
                  <option key={channel.id} value={channel.id}>
                    {channel.name} ({channel.slug})
                  </option>
                ))}
              </select>
            </div>
            <div className="field">
              <label htmlFor="archive_source_name">Name</label>
              <input id="archive_source_name" name="name" className="input" value={archiveSourceForm.name} onChange={handleArchiveSourceFormChange} required />
            </div>
            <div className="field">
              <label htmlFor="archive_source_query">Archive query</label>
              <textarea id="archive_source_query" name="query" className="input" rows={3} value={archiveSourceForm.query} onChange={handleArchiveSourceFormChange} required />
            </div>
            <div
              style={{
                border: '1px solid rgba(15, 23, 42, 0.12)',
                borderRadius: '8px',
                padding: '0.55rem 0.6rem',
                marginBottom: '0.6rem',
                display: 'grid',
                gap: '0.5rem',
              }}
            >
              <strong style={{ fontSize: '0.9rem' }}>Query preset: only own uploads</strong>
              <input
                className="input"
                value={archiveOwnUploader}
                onChange={(event) => setArchiveOwnUploader(event.target.value)}
                placeholder="uploader email, e.g. info@yourdomain.com"
              />
              <button type="button" className="btn btn-muted" onClick={applyOwnUploadsQueryPreset}>
                Apply own-uploads preset
              </button>
            </div>
            <div className="field">
              <label htmlFor="archive_source_weight">Weight</label>
              <input id="archive_source_weight" name="weight" className="input" type="number" min="1" max="100" value={archiveSourceForm.weight} onChange={handleArchiveSourceFormChange} />
            </div>
            <button type="submit" className="btn btn-primary">
              {editingArchiveSource ? 'Update source' : 'Save source'}
            </button>
          </form>

          <form onSubmit={submitArchiveQueuePreview} className="card" style={{ boxShadow: 'var(--shadow-sm)' }}>
            <h4 style={{ marginBottom: '0.6rem' }}>Queue preview</h4>
            <div className="field">
              <label htmlFor="archive_preview_channel">Channel</label>
              <select id="archive_preview_channel" name="channel_id" className="input" value={archivePreviewForm.channel_id} onChange={handleArchivePreviewFormChange} required>
                <option value="">Select channel…</option>
                {archiveChannels.map((channel) => (
                  <option key={channel.id} value={channel.id}>
                    {channel.name} ({channel.slug})
                  </option>
                ))}
              </select>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.6rem' }}>
              <div className="field">
                <label htmlFor="archive_preview_count">Count</label>
                <input id="archive_preview_count" name="count" className="input" type="number" min="1" max="50" value={archivePreviewForm.count} onChange={handleArchivePreviewFormChange} />
              </div>
              <div className="field">
                <label htmlFor="archive_preview_seed">Seed (optional)</label>
                <input id="archive_preview_seed" name="seed" className="input" value={archivePreviewForm.seed} onChange={handleArchivePreviewFormChange} placeholder="overnight-2026-02-10" />
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.6rem' }}>
              <div className="field">
                <label htmlFor="archive_preview_min_duration">Min sec</label>
                <input id="archive_preview_min_duration" name="min_duration_seconds" className="input" type="number" min="1" value={archivePreviewForm.min_duration_seconds} onChange={handleArchivePreviewFormChange} />
              </div>
              <div className="field">
                <label htmlFor="archive_preview_max_duration">Max sec</label>
                <input id="archive_preview_max_duration" name="max_duration_seconds" className="input" type="number" min="1" value={archivePreviewForm.max_duration_seconds} onChange={handleArchivePreviewFormChange} />
              </div>
            </div>
            <div className="field">
              <label htmlFor="archive_preview_license_allowlist">License filters (comma separated)</label>
              <input id="archive_preview_license_allowlist" name="license_allowlist" className="input" value={archivePreviewForm.license_allowlist} onChange={handleArchivePreviewFormChange} placeholder="publicdomain, creativecommons.org" />
            </div>
            <p className="text-muted" style={{ marginTop: '-0.1rem' }}>
              Active sources for selected channel: {previewActiveSourceCount}
            </p>
            {archivePreviewForm.channel_id && previewActiveSourceCount === 0 && (
              <p className="text-muted" style={{ color: 'var(--danger)' }}>
                No active sources on this channel. Preview/shuffle will return empty.
              </p>
            )}
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <input type="checkbox" name="exclude_recent" checked={archivePreviewForm.exclude_recent} onChange={handleArchivePreviewFormChange} />
              <span>Exclude recently played</span>
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <input type="checkbox" name="debug" checked={archivePreviewForm.debug} onChange={handleArchivePreviewFormChange} />
              <span>Debug mode (show skip reasons)</span>
            </label>
            <button type="submit" className="btn btn-primary" disabled={archivePreview.loading}>
              {archivePreview.loading ? 'Generating…' : 'Preview queue'}
            </button>
          </form>
        </div>

        <div className="card" style={{ boxShadow: 'var(--shadow-sm)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '0.6rem' }}>
            <h4 style={{ marginBottom: 0 }}>Current sources</h4>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.9rem' }}>
                <input
                  type="checkbox"
                  checked={archiveSources.length > 0 && selectedArchiveSourceIds.length === archiveSources.length}
                  onChange={(event) => handleSelectAllArchiveSources(event.target.checked)}
                  disabled={archiveSources.length === 0 || bulkDeletingArchiveSources}
                />
                <span>Select all</span>
              </label>
              <button
                type="button"
                className="btn btn-muted"
                onClick={handleDeleteSelectedArchiveSources}
                disabled={selectedArchiveSourceIds.length === 0 || bulkDeletingArchiveSources}
              >
                {bulkDeletingArchiveSources
                  ? 'Deleting…'
                  : `Delete selected (${selectedArchiveSourceIds.length})`}
              </button>
            </div>
          </div>
          {archiveSources.length === 0 ? (
            <p className="text-muted">No archive sources configured yet.</p>
          ) : (
            <div style={{ display: 'grid', gap: '0.6rem' }}>
              {archiveSources.map((source) => (
                <div
                  key={source.id}
                  style={{
                    border: '1px solid rgba(15, 23, 42, 0.12)',
                    borderRadius: '10px',
                    padding: '0.65rem 0.8rem',
                    display: 'grid',
                    gap: '0.35rem',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.5rem' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.45rem' }}>
                      <input
                        type="checkbox"
                        checked={selectedArchiveSourceIds.includes(source.id)}
                        onChange={(event) =>
                          handleToggleArchiveSourceSelection(source.id, event.target.checked)
                        }
                        disabled={bulkDeletingArchiveSources}
                      />
                      <strong>{source.name}</strong>
                    </label>
                    <div style={{ display: 'flex', gap: '0.45rem' }}>
                      <button type="button" className="btn btn-muted" onClick={() => startArchiveSourceEdit(source)}>
                        Edit
                      </button>
                      <button type="button" className="btn btn-muted" onClick={() => handleToggleArchiveSource(source.id)}>
                        {source.is_active ? 'Disable' : 'Enable'}
                      </button>
                      <button type="button" className="btn btn-muted" onClick={() => handleDeleteArchiveSource(source.id)}>
                        Delete
                      </button>
                    </div>
                  </div>
                  <div className="text-muted" style={{ fontSize: '0.9rem' }}>
                    {source.channel?.name || 'Channel'} · Weight {source.weight} · {source.is_active ? 'Active' : 'Disabled'}
                  </div>
                  <code style={{ whiteSpace: 'pre-wrap' }}>{source.query}</code>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="card" style={{ boxShadow: 'var(--shadow-sm)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.6rem' }}>
            <h4 style={{ marginBottom: 0 }}>Shuffle history</h4>
            <button
              type="button"
              className="btn btn-muted"
              onClick={() => loadArchiveHistory(archivePreviewForm.channel_id || null)}
              disabled={archiveHistory.loading}
            >
              {archiveHistory.loading ? 'Refreshing…' : 'Refresh history'}
            </button>
          </div>
          <p className="text-muted" style={{ marginTop: '0.45rem' }}>
            Last {archiveHistory.items.length} of {archiveHistory.total} auto-shuffle plays.
          </p>
          {archiveHistory.items.length === 0 ? (
            <p className="text-muted">No auto-shuffle history yet for this channel.</p>
          ) : (
            <div style={{ display: 'grid', gap: '0.5rem', marginTop: '0.35rem' }}>
              {archiveHistory.items.map((entry) => (
                <div
                  key={entry.id}
                  style={{
                    border: '1px solid rgba(15, 23, 42, 0.1)',
                    borderRadius: '10px',
                    padding: '0.6rem 0.75rem',
                  }}
                >
                  <strong>{entry.metadata?.title || entry.archive_identifier}</strong>
                  <div className="text-muted" style={{ fontSize: '0.9rem' }}>
                    {entry.archive_identifier} · {entry.archive_file_name || 'n/a'} · {formatDuration(entry.duration_seconds)} · {formatDateTime(entry.played_at)}
                  </div>
                  <div className="text-muted" style={{ fontSize: '0.85rem' }}>
                    Source: {entry.source?.name || 'n/a'} · Channel: {entry.channel?.name || 'n/a'}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {archivePreview.meta && (
          <div className="card" style={{ boxShadow: 'var(--shadow-sm)' }}>
            <h4 style={{ marginBottom: '0.6rem' }}>
              Preview results ({archivePreview.meta.count_returned}/{archivePreview.meta.count_requested})
            </h4>
            {archivePreview.meta?.debug && (
              <div
                style={{
                  border: '1px solid rgba(15, 23, 42, 0.12)',
                  borderRadius: '10px',
                  padding: '0.65rem 0.8rem',
                  marginBottom: '0.75rem',
                  background: 'rgba(15, 23, 42, 0.03)',
                }}
              >
                <strong>Debug totals</strong>
                <div className="text-muted" style={{ fontSize: '0.9rem', marginTop: '0.25rem' }}>
                  Sources: {archivePreview.meta.debug?.totals?.sources_considered ?? 0} · Docs seen: {archivePreview.meta.debug?.totals?.docs_seen ?? 0}
                </div>
                <pre style={{ marginTop: '0.5rem', whiteSpace: 'pre-wrap', fontSize: '0.85rem' }}>
                  {JSON.stringify(archivePreview.meta.debug?.totals?.skipped ?? {}, null, 2)}
                </pre>
              </div>
            )}
            {archivePreview.items.length === 0 ? (
              <div style={{ display: 'grid', gap: '0.5rem' }}>
                <p className="text-muted">No candidates matched this preview request.</p>
                {archivePreviewLikelyRecentFilterBlock && (
                  <div
                    style={{
                      border: '1px solid rgba(245, 158, 11, 0.35)',
                      background: 'rgba(245, 158, 11, 0.08)',
                      borderRadius: '10px',
                      padding: '0.6rem 0.75rem',
                    }}
                  >
                    <strong style={{ display: 'block', marginBottom: '0.2rem' }}>
                      All candidates were likely filtered as recently played.
                    </strong>
                    <p className="text-muted" style={{ margin: 0 }}>
                      Try unchecking <strong>Exclude recently played</strong> for this preview, or lower
                      <strong> No-repeat hrs</strong> in the channel settings.
                    </p>
                  </div>
                )}
              </div>
            ) : (
              <div style={{ display: 'grid', gap: '0.6rem' }}>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr auto auto auto',
                    gap: '0.6rem',
                    alignItems: 'end',
                  }}
                >
                  <label className="field" style={{ marginBottom: 0 }}>
                    <span>Queue start at</span>
                    <input
                      type="datetime-local"
                      className="input"
                      value={archiveQueueStartAt}
                      onChange={(event) => setArchiveQueueStartAt(event.target.value)}
                    />
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.45rem', fontWeight: 600 }}>
                    <input
                      type="checkbox"
                      checked={allArchivePreviewItemsSelected}
                      onChange={(event) => handleSelectAllArchivePreviewItems(event.target.checked)}
                    />
                    <span>Select all</span>
                  </label>
                  <button
                    type="button"
                    className="btn btn-primary"
                    disabled={queueingArchivePreview || selectedArchivePreviewCount === 0}
                    onClick={queueSelectedArchivePreviewToTimeline}
                  >
                    {queueingArchivePreview
                      ? 'Queueing…'
                      : `Review queue (${selectedArchivePreviewCount})`}
                  </button>
                  <button
                    type="button"
                    className="btn btn-muted"
                    disabled={queueingArchivePreview || selectedArchivePreviewCount === 0}
                    onClick={() => queueSelectedArchivePreviewToTimeline({ afterLastScheduled: true })}
                  >
                    {queueingArchivePreview ? 'Queueing…' : 'Review after last slot'}
                  </button>
                </div>
                {archivePreview.items.map((item, index) => (
                  <div
                    key={buildArchivePreviewItemKey(item, index)}
                    style={{ border: '1px solid rgba(15, 23, 42, 0.1)', borderRadius: '10px', padding: '0.6rem 0.75rem' }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.75rem' }}>
                      <strong>
                        {index + 1}. {item.title || item.identifier}
                      </strong>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                        <input
                          type="checkbox"
                          checked={selectedArchivePreviewItemKeys.includes(buildArchivePreviewItemKey(item, index))}
                          onChange={(event) =>
                            handleToggleArchivePreviewItemSelection(
                              buildArchivePreviewItemKey(item, index),
                              event.target.checked
                            )
                          }
                        />
                        <span style={{ fontSize: '0.9rem' }}>Queue</span>
                      </label>
                    </div>
                    <div className="text-muted" style={{ fontSize: '0.9rem' }}>
                      {item.identifier} · {item.file_name} · {formatDuration(item.duration_seconds)}
                    </div>
                    <code style={{ whiteSpace: 'pre-wrap' }}>{item.source_url}</code>
                    <div style={{ marginTop: '0.5rem', display: 'flex', gap: '0.5rem' }}>
                      <button
                        type="button"
                        className="btn btn-muted"
                        onClick={() => applyArchivePreviewAsSchedulePreset(item)}
                      >
                        Use as schedule preset
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
      )}

      <div className="card" style={{ display: 'grid', gap: '1.5rem' }}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '1rem',
          }}
        >
          {isLibraryPage && (
          <form onSubmit={submitProgram} className="card" style={{ boxShadow: 'var(--shadow-sm)' }}>
            <h3 style={{ marginBottom: '0.5rem' }}>{editingProgram ? 'Edit program' : 'Create program'}</h3>
            <p className="text-muted" style={{ marginBottom: '1rem' }}>
              {editingProgram ? 'Update an existing program entry.' : 'Store metadata for prerecorded content.'}
            </p>

            {editingProgram && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  padding: '0.75rem 1rem',
                  background: 'rgba(15, 23, 42, 0.04)',
                  borderRadius: 'var(--radius-sm)',
                  marginBottom: '0.5rem',
                }}
              >
                <span style={{ fontWeight: 600 }}>Editing “{editingProgram.title}”</span>
                <button type="button" className="btn btn-muted" onClick={resetProgramForm} disabled={saving.program}>
                  Cancel edit
                </button>
              </div>
            )}

            <div className="field">
              <label htmlFor="title">Title</label>
              <input
                id="title"
                name="title"
                className="input"
                value={programForm.title}
                onChange={handleProgramChange}
                required
              />
            </div>

            <div className="field">
              <label htmlFor="category">Category</label>
              <select
                id="category"
                name="category"
                className="input"
                value={programForm.category}
                onChange={handleProgramChange}
              >
                {programCategoryOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="field">
              <label htmlFor="media_type">Media type</label>
              <select
                id="media_type"
                name="media_type"
                className="input"
                value={programForm.media_type}
                onChange={handleProgramChange}
              >
                <option value="video">Video</option>
                <option value="image">Image</option>
                <option value="web">Web</option>
                <option value="playlist">Playlist</option>
              </select>
            </div>

            <div className="field">
              <label htmlFor="odysee_url">Odysee link (optional)</label>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <input
                  id="odysee_url"
                  name="odysee_url"
                  className="input"
                  value={odyseeUrl}
                  onChange={(e) => setOdyseeUrl(e.target.value)}
                  placeholder="https://odysee.com/@channel/video:hash"
                />
                <button
                  type="button"
                  className="btn btn-muted"
                  onClick={handleOdyseeConvert}
                  disabled={!odyseeUrl || odyseeLookup.loading}
                >
                  {odyseeLookup.loading ? 'Checking…' : 'Use link'}
                </button>
              </div>
              <p className="text-muted" style={{ marginTop: '0.35rem' }}>
                Converts to an embeddable URL and sets media type to Web.
              </p>
            </div>

            <div className="field">
              <label htmlFor="youtube_url_program">YouTube link (optional)</label>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <input
                  id="youtube_url_program"
                  name="youtube_url_program"
                  className="input"
                  value={youtubeUrl}
                  onChange={(e) => setYoutubeUrl(e.target.value)}
                  placeholder="https://www.youtube.com/watch?v=..."
                />
                <button
                  type="button"
                  className="btn btn-muted"
                  onClick={handleYouTubeApply}
                  disabled={!youtubeUrl}
                >
                  Use link
                </button>
              </div>
              <p className="text-muted" style={{ marginTop: '0.35rem' }}>
                Converts to an embeddable URL and sets media type to Web.
              </p>
            </div>

            <div className="field">
              <label htmlFor="archive_url">Archive.org MP4 (optional)</label>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <input
                  id="archive_url"
                  name="archive_url"
                  className="input"
                  value={archiveUrl}
                  onChange={(e) => setArchiveUrl(e.target.value)}
                  placeholder="https://archive.org/download/{id}/{file}.mp4"
                />
                <button
                  type="button"
                  className="btn btn-muted"
                  onClick={handleArchiveApply}
                  disabled={!archiveUrl}
                >
                  Use MP4
                </button>
                <button
                  type="button"
                  className="btn btn-muted"
                  onClick={handleArchiveFetchFiles}
                  disabled={archiveLookup.loading || (!archiveUrl && !archiveIdentifier)}
                >
                  {archiveLookup.loading ? 'Fetching…' : 'Fetch files'}
                </button>
              </div>
              <p className="text-muted" style={{ marginTop: '0.35rem' }}>
                Paste a direct download URL or a details page to build one.
              </p>
            </div>
            {archiveIdentifier && (
              <div className="field">
                <label htmlFor="archive_file">Archive file name</label>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <input
                    id="archive_file"
                    name="archive_file"
                    className="input"
                    value={archiveFileName}
                    onChange={(e) => setArchiveFileName(e.target.value)}
                    placeholder={`${archiveIdentifier}.mp4`}
                  />
                  <button type="button" className="btn btn-muted" onClick={handleArchiveBuildFromDetails}>
                    Build MP4
                  </button>
                </div>
                <p className="text-muted" style={{ marginTop: '0.35rem' }}>
                  Uses `https://archive.org/download/{archiveIdentifier}/{"{file}"}.mp4`.
                </p>
              </div>
            )}
            {archiveLookup.files.length > 0 && (
              <div className="field">
                <label htmlFor="archive_file_pick">Archive MP4 list</label>
                <select
                  id="archive_file_pick"
                  name="archive_file_pick"
                  className="input"
                  value={archiveFileName}
                  onChange={(e) => setArchiveFileName(e.target.value)}
                >
                  <option value="">Select a file…</option>
                  {archiveLookup.files.map((file, index) => {
                    const sizeLabel = file.size_bytes
                      ? ` · ${Math.round(file.size_bytes / 1024 / 1024)} MB`
                      : '';
                    return (
                      <option key={file.name ?? `${index}`} value={file.name}>
                        {index + 1}. {file.name}
                        {sizeLabel}
                      </option>
                    );
                  })}
                </select>
                <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
                  <input
                    id="archive_pick_index"
                    name="archive_pick_index"
                    className="input"
                    value={archivePickIndex}
                    onChange={(e) => setArchivePickIndex(e.target.value)}
                    placeholder="Pick #"
                    style={{ maxWidth: '120px' }}
                  />
                  <button type="button" className="btn btn-muted" onClick={handleArchivePickIndexApply}>
                    Use #
                  </button>
                </div>
                <p className="text-muted" style={{ marginTop: '0.35rem' }}>
                  Select a file by name or number, then click Build MP4.
                </p>
              </div>
            )}

            <div className="field">
              <label htmlFor="source_url">Source URL</label>
              <input
                id="source_url"
                name="source_url"
                className="input"
                value={programForm.source_url}
                onChange={handleProgramChange}
                placeholder="https://cdn.example.com/promo.mp4"
              />
            </div>

            <div className="field">
              <label htmlFor="thumbnail_url">Thumbnail URL</label>
              <input
                id="thumbnail_url"
                name="thumbnail_url"
                className="input"
                value={programForm.thumbnail_url}
                onChange={handleProgramChange}
                placeholder="https://cdn.example.com/promo.jpg"
              />
              <button
                className="btn btn-muted"
                type="button"
                style={{ alignSelf: 'flex-start', marginTop: '0.5rem' }}
                onClick={() => setProgramThumbnailPickerOpen(true)}
              >
                Choose from Media Library
              </button>
            </div>

            <div className="field">
              <label>Preview upload</label>
              <div
                style={{
                  borderRadius: '12px',
                  border: '1px solid rgba(15, 23, 42, 0.12)',
                  padding: '0.75rem',
                  background: 'rgba(15, 23, 42, 0.04)',
                  display: 'grid',
                  gap: '0.75rem',
                }}
              >
                {programFormPreview?.isVideo ? (
                  <video
                    key={programFormPreview.mediaUrl}
                    src={programFormPreview.mediaUrl}
                    poster={programForm.thumbnail_url}
                    style={{ width: '100%', maxHeight: 240, background: '#000', borderRadius: '10px' }}
                    controls
                    muted
                    playsInline
                  />
                ) : programFormPreview?.isImage ? (
                  <img
                    src={programFormPreview.mediaUrl}
                    alt={programForm.title || 'Program preview'}
                    style={{ width: '100%', maxHeight: 240, objectFit: 'contain', borderRadius: '10px' }}
                  />
                ) : programFormPreview?.isEmbeddable ? (
                  <iframe
                    key={programFormPreview.odyseeEmbed ?? programFormPreview.mediaUrl}
                    src={programFormPreview.odyseeEmbed ?? programFormPreview.mediaUrl}
                    title={programForm.title || 'Program preview'}
                    style={{ width: '100%', minHeight: 180, borderRadius: '10px', background: '#000' }}
                    frameBorder="0"
                    allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                ) : programForm.thumbnail_url ? (
                  <img
                    src={programForm.thumbnail_url}
                    alt={programForm.title || 'Program preview'}
                    style={{ width: '100%', maxHeight: 240, objectFit: 'contain', borderRadius: '10px' }}
                  />
                ) : (
                  <div className="text-muted">Add a source URL or thumbnail to preview this program.</div>
                )}
              </div>
            </div>

            <div className="field">
              <label htmlFor="duration_hms">Duration (HH:MM:SS)</label>
              <input
                id="duration_hms"
                name="duration_hms"
                className="input"
                type="text"
                inputMode="numeric"
                placeholder="00:00:00"
                value={programForm.duration_hms}
                onChange={handleProgramChange}
              />
            </div>

            <div className="field">
              <label htmlFor="description">Description</label>
              <textarea
                id="description"
                name="description"
                className="input"
                rows={3}
                value={programForm.description}
                onChange={handleProgramChange}
              />
            </div>

            <div className="field">
              <label>Usage</label>
              <div style={{ display: 'flex', gap: '1rem' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                  <input
                    type="radio"
                    name="usage"
                    value="primary"
                    checked={programForm.usage === 'primary'}
                    onChange={handleProgramChange}
                  />
                  <span>Primary</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                  <input
                    type="radio"
                    name="usage"
                    value="filler"
                    checked={programForm.usage === 'filler'}
                    onChange={handleProgramChange}
                  />
                  <span>Filler</span>
                </label>
              </div>
            </div>

            {programForm.usage === 'filler' && (
              <>
                <div className="field">
                  <label htmlFor="filler_tags">Tags (comma separated)</label>
                  <input
                    id="filler_tags"
                    name="filler_tags"
                    className="input"
                    value={programForm.filler_tags}
                    onChange={handleProgramChange}
                    placeholder="promo, overnight, upbeat"
                  />
                </div>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <input
                    type="checkbox"
                    name="is_loopable"
                    checked={programForm.is_loopable}
                    onChange={handleProgramChange}
                  />
                  <span>Allow looping / trim for gap fills</span>
                </label>
              </>
            )}

            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <button type="submit" className="btn btn-primary" disabled={saving.program}>
                {saving.program
                  ? editingProgram
                    ? 'Updating…'
                    : 'Saving…'
                  : editingProgram
                  ? 'Update program'
                  : 'Save program'}
              </button>
              {editingProgram && (
                <button
                  type="button"
                  className="btn btn-muted"
                  onClick={resetProgramForm}
                  disabled={saving.program}
                >
                  Exit edit
                </button>
              )}
            </div>
            {feedback.program && (
              <p style={{ marginTop: '0.5rem', color: 'var(--success)' }}>{feedback.program}</p>
            )}
          </form>
          )}

          {!isLibraryPage && (
          <form onSubmit={submitSchedule} className="card" style={{ boxShadow: 'var(--shadow-sm)' }}>
            <h3 style={{ marginBottom: '0.5rem' }}>{editingSchedule ? 'Edit slot' : 'Schedule slot'}</h3>
            <p className="text-muted" style={{ marginBottom: '1rem' }}>
              {editingSchedule
                ? 'Adjust timing or metadata for this scheduled window.'
                : 'Pick a program or configure a live window with OBS hints.'}
            </p>

            {editingSchedule && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  padding: '0.75rem 1rem',
                  background: 'rgba(15, 23, 42, 0.04)',
                  borderRadius: 'var(--radius-sm)',
                  marginBottom: '0.5rem',
                }}
              >
                <span style={{ fontWeight: 600 }}>Editing “{editingSchedule.title}”</span>
                <button
                  type="button"
                  className="btn btn-muted"
                  onClick={() => resetScheduleForm(false)}
                  disabled={saving.schedule}
                >
                  Cancel edit
                </button>
              </div>
            )}

            <div className="field">
              <label htmlFor="type">Slot type</label>
              <select
                id="type"
                name="type"
                className="input"
                value={scheduleForm.type}
                onChange={handleScheduleChange}
              >
                <option value="program">Program</option>
                <option value="live">Live stream</option>
              </select>
            </div>

            <div className="field">
              <label htmlFor="schedule_title">Title</label>
              <input
                id="schedule_title"
                name="title"
                className="input"

                value={scheduleForm.title}
                onChange={handleScheduleChange}
                required
              />
            </div>

            {scheduleForm.type === 'program' && (
              <div className="field">
                <label htmlFor="program_id">Program</label>
                <select
                  id="program_id"
                  name="program_id"
                  className="input"
                  value={scheduleForm.program_id}
                  onChange={handleScheduleChange}
                  required
                >
                  <option value="">Select a program</option>
                  {sortedPrograms.map((program) => (
                    <option key={program.id} value={program.id}>
                      {program.title}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {scheduleForm.type === 'live' && (
              <>
                <div className="field">
                  <label htmlFor="live_input_url">OBS input / RTMP URL</label>
                  <input
                    id="live_input_url"
                    name="live_input_url"
                    className="input"
                    value={scheduleForm.live_input_url}
                    onChange={handleScheduleChange}
                    required
                  />
                </div>

                <div className="field">
                  <label htmlFor="youtube_url">YouTube URL</label>
                  <input
                    id="youtube_url"
                    name="youtube_url"
                    className="input"
                    type="url"
                    value={scheduleForm.youtube_url}
                    onChange={handleScheduleChange}
                    placeholder="https://www.youtube.com/watch?v=..."
                  />
                </div>

                <div className="field">
                  <label htmlFor="jitsi_room">Jitsi room</label>
                  <input
                    id="jitsi_room"
                    name="jitsi_room"
                    className="input"
                    value={scheduleForm.jitsi_room}
                    onChange={handleScheduleChange}
                    placeholder="hem-tv-live-room"
                  />
                </div>

                <div className="field">
                  <label htmlFor="jitsi_join_url">Jitsi join link</label>
                  <input
                    id="jitsi_join_url"
                    name="jitsi_join_url"
                    className="input"
                    type="url"
                    value={scheduleForm.jitsi_join_url}
                    onChange={handleScheduleChange}
                    placeholder="https://meet.jit.si/hem-tv-live-room"
                  />
                </div>
              </>
            )}

            {scheduleForm.type === 'program' && selectedProgram && (
              <div className="field">
                <label>Preview selected program</label>
                <div
                  style={{
                    borderRadius: '12px',
                    border: '1px solid rgba(15, 23, 42, 0.12)',
                    padding: '0.75rem',
                    background: 'rgba(15, 23, 42, 0.04)',
                    display: 'grid',
                    gap: '0.75rem',
                  }}
                >
                  <div style={{ fontWeight: 600 }}>{selectedProgram.title}</div>
                  {selectedProgramPreview?.isVideo ? (
                    <video
                      key={selectedProgramPreview.mediaUrl}
                      src={selectedProgramPreview.mediaUrl}
                      poster={selectedProgram.thumbnail_url}
                      style={{ width: '100%', maxHeight: 280, background: '#000', borderRadius: '10px' }}
                      controls
                      muted
                      playsInline
                    />
                  ) : selectedProgramPreview?.isImage ? (
                    <img
                      src={selectedProgramPreview.mediaUrl}
                      alt={selectedProgram.title}
                      style={{ width: '100%', maxHeight: 280, objectFit: 'contain', borderRadius: '10px' }}
                    />
                  ) : selectedProgramPreview?.isEmbeddable ? (
                    <iframe
                      key={selectedProgramPreview.odyseeEmbed ?? selectedProgramPreview.mediaUrl}
                      src={selectedProgramPreview.odyseeEmbed ?? selectedProgramPreview.mediaUrl}
                      title={selectedProgram.title}
                      style={{ width: '100%', minHeight: 220, borderRadius: '10px', background: '#000' }}
                      frameBorder="0"
                      allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  ) : selectedProgram.thumbnail_url ? (
                    <img
                      src={selectedProgram.thumbnail_url}
                      alt={selectedProgram.title}
                      style={{ width: '100%', maxHeight: 280, objectFit: 'contain', borderRadius: '10px' }}
                    />
                  ) : (
                    <div className="text-muted">No preview available for this media type.</div>
                  )}
                  {selectedProgram.description && (
                    <p className="text-muted" style={{ margin: 0 }}>
                      {selectedProgram.description}
                    </p>
                  )}
                </div>
              </div>
            )}

            <div className="field">
              <label htmlFor="starts_at">Starts at</label>
              <input
                type="datetime-local"
                id="starts_at"
                name="starts_at"
                className="input"
                value={scheduleForm.starts_at}
                onChange={handleScheduleChange}
                step="0.001"
                required
              />
            </div>

            <div className="field">
              <label htmlFor="ends_at">Ends at</label>
              <input
                type="datetime-local"
                id="ends_at"
                name="ends_at"
                className="input"
                value={scheduleForm.ends_at}
                onChange={handleScheduleChange}
                step="0.001"
                required
              />
            </div>

            {scheduleForm.type === 'program' && (
              <>
                <div className="field">
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <input
                      type="checkbox"
                      name="auto_filler_enabled"
                      checked={scheduleForm.auto_filler_enabled}
                      onChange={handleScheduleChange}
                    />
                    <span>Auto-fill underruns</span>
                  </label>
                  <p className="text-muted" style={{ marginTop: '0.35rem' }}>
                    Fill any shortfall using filler programs.
                  </p>
                </div>

                {scheduleForm.auto_filler_enabled && (
                  <>
                    <div className="field">
                      <label htmlFor="auto_filler_category">Filler category</label>
                      <select
                        id="auto_filler_category"
                        name="auto_filler_category"
                        className="input"
                        value={scheduleForm.auto_filler_category}
                        onChange={handleScheduleChange}
                      >
                        {fillerCategoryOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="field">
                      <label htmlFor="auto_filler_tags">Filler tags (comma separated)</label>
                      <input
                        id="auto_filler_tags"
                        name="auto_filler_tags"
                        className="input"
                        value={scheduleForm.auto_filler_tags}
                        onChange={handleScheduleChange}
                        placeholder="worship, upbeat"
                      />
                    </div>
                  </>
                )}
              </>
            )}

            <div className="field">
              <label htmlFor="obs_scene">OBS scene</label>
              <input
                id="obs_scene"
                name="obs_scene"
                className="input"
                value={scheduleForm.obs_scene}
                onChange={handleScheduleChange}
                placeholder="Live Desk"
              />
            </div>

            <div className="field">
              <label htmlFor="obs_profile">OBS profile</label>
              <input
                id="obs_profile"
                name="obs_profile"
                className="input"
                value={scheduleForm.obs_profile}
                onChange={handleScheduleChange}
                placeholder="HEM-TV-1080p"
              />
            </div>

            <div className="field">
              <label htmlFor="recurrence_type">Repeat</label>
              <select
                id="recurrence_type"
                name="recurrence_type"
                className="input"
                value={scheduleForm.recurrence_type}
                onChange={handleScheduleChange}
                disabled={Boolean(editingSchedule)}
              >
                <option value="none">Do not repeat</option>
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
              </select>
              {editingSchedule && (
                <p className="text-muted" style={{ marginTop: '0.35rem' }}>
                  Repeat settings cannot be changed for an existing slot.
                </p>
              )}
            </div>

            {scheduleForm.recurrence_type !== 'none' && (
              <div className="field">
                <label htmlFor="recurrence_count">Occurrences</label>
                <input
                  id="recurrence_count"
                  name="recurrence_count"
                  className="input"
                  type="number"
                  min="1"
                  max="30"
                  value={scheduleForm.recurrence_count}
                  onChange={handleScheduleChange}
                  disabled={Boolean(editingSchedule)}
                />
              </div>
            )}

            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <button type="submit" className="btn btn-primary" disabled={saving.schedule}>
                {saving.schedule
                  ? editingSchedule
                    ? 'Updating…'
                    : 'Scheduling…'
                  : editingSchedule
                  ? 'Update slot'
                  : 'Schedule slot'}
              </button>
              {editingSchedule && (
                <button
                  type="button"
                  className="btn btn-muted"
                  onClick={() => resetScheduleForm(false)}
                  disabled={saving.schedule}
                >
                  Exit edit
                </button>
              )}
            </div>
            {feedback.schedule && (
              <p style={{ marginTop: '0.5rem', color: 'var(--success)' }}>{feedback.schedule}</p>
            )}
          </form>
          )}
        </div>
      </div>

      <div>
        <div>
          {!isLibraryPage && (
            <div className="card">
              <div className="calendar-toolbar">
                <div>
                  <h3>Schedule overview</h3>
                  <p className="text-muted">
                    {scheduleWindow?.start
                      ? `Window: ${new Date(scheduleWindow.start).toLocaleString()} to ${new Date(
                          scheduleWindow.end
                        ).toLocaleString()}`
                      : `Week of ${formatRangeLabel(calendarRange)}`}
                  </p>
                  <p className="text-muted" style={{ marginTop: '0.25rem' }}>
                    Server time:{' '}
                    {serverTime ? new Date(serverTime).toLocaleString() : '—'}
                    {serverTimezone ? ` (${serverTimezone})` : ''}
                  </p>
                </div>
                <div className="calendar-toolbar-actions">
                  <div className="calendar-nav">
                    <button type="button" className="btn btn-muted" onClick={() => handleCalendarShift(-1)}>
                      ← Prev
                    </button>
                    <button
                      type="button"
                      className="btn btn-muted"
                      onClick={() => setCalendarRange(getCalendarRange(new Date()))}
                    >
                      Today
                    </button>
                    <button type="button" className="btn btn-muted" onClick={() => handleCalendarShift(1)}>
                      Next →
                    </button>
                  </div>
                  <button type="button" className="btn btn-primary" onClick={() => loadSchedule()}>
                    Refresh
                  </button>
                </div>
              </div>

              {loading.schedule ? (
                <div>Loading schedule…</div>
              ) : viewMode === 'calendar' ? (
                <div className="calendar-grid">
                  {calendarDays.map((day) => {
                    const events = eventsByDay[day.dateKey] ?? [];
                    return (
                      <div key={day.dateKey} className="calendar-cell">
                        <div className="calendar-cell-header">
                          <span>{day.label}</span>
                          <strong>{day.day}</strong>
                        </div>
                        <div className="calendar-events">
                          {events.length === 0 && <p className="text-muted">No slots</p>}
                          {events.map((slot) => {
                            const effectiveStatus = resolveSlotStatus(slot);
                            return (
                            <div
                              key={slot.id}
                              className={`calendar-event status-${effectiveStatus} ${
                                slot.is_auto_filler ? 'calendar-event-filler' : ''
                              }`}
                            >
                              <strong>{slot.title}</strong>
                              <p>{formatTimeRange(slot.starts_at, slot.ends_at)}</p>
                              <p className="text-muted">
                                {slot.type === 'live' ? 'Live stream' : slot.program?.title ?? 'Program'}
                              </p>
                              {slot.jitsi_room && (
                                <p className="text-muted" style={{ fontSize: '0.85rem' }}>
                                  Jitsi room: <strong>{slot.jitsi_room}</strong>
                                </p>
                              )}
                              {slot.jitsi_join_url && (
                                <p style={{ fontSize: '0.85rem' }}>
                                  <a href={slot.jitsi_join_url} target="_blank" rel="noreferrer">
                                    Join link
                                  </a>
                                </p>
                              )}
                              {slot.youtube_url && (
                                <p style={{ fontSize: '0.85rem' }}>
                                  <a href={slot.youtube_url} target="_blank" rel="noreferrer">
                                    YouTube link
                                  </a>
                                </p>
                              )}
                              {slot.is_auto_filler && (
                                <span className="badge badge-muted" style={{ marginTop: '0.35rem' }}>
                                  Auto filler
                                </span>
                              )}
                              {slot.recurrence_type && (
                                <span className="badge badge-muted" style={{ marginTop: '0.25rem' }}>
                                  {slot.recurrence_type === 'weekly' ? 'Weekly' : 'Daily'}
                                  {slot.recurrence_sequence ? ` · #${slot.recurrence_sequence}` : ''}
                                </span>
                              )}
                            </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : scheduleItems.length ? (
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Window</th>
                      <th>Type</th>
                      <th>Repeat</th>
                      <th>Status</th>
                      <th style={{ textAlign: 'right' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {scheduleItems.map((slot) => {
                      const effectiveStatus = resolveSlotStatus(slot);
                      return (
                      <tr key={slot.id}>
                        <td>
                          <strong>{slot.title}</strong>
                          {slot.is_auto_filler && (
                            <span className="badge badge-muted" style={{ marginLeft: '0.5rem' }}>
                              Auto filler
                            </span>
                          )}
                          <p style={{ fontSize: '0.85rem' }}>{formatDateRange(slot.starts_at, slot.ends_at)}</p>
                          <p style={{ fontSize: '0.85rem' }}>{formatTimeRange(slot.starts_at, slot.ends_at)}</p>
                          {slot.jitsi_room && (
                            <p className="text-muted" style={{ fontSize: '0.85rem' }}>
                              Jitsi room: <strong>{slot.jitsi_room}</strong>
                            </p>
                          )}
                          {slot.jitsi_join_url && (
                            <p style={{ fontSize: '0.85rem' }}>
                              <a href={slot.jitsi_join_url} target="_blank" rel="noreferrer">
                                Join link
                              </a>
                            </p>
                          )}
                          {slot.youtube_url && (
                            <p style={{ fontSize: '0.85rem' }}>
                              <a href={slot.youtube_url} target="_blank" rel="noreferrer">
                                YouTube link
                              </a>
                            </p>
                          )}
                        </td>
                        <td>{slot.type === 'live' ? 'Live stream' : slot.program?.title ?? 'Program'}</td>
                        <td>
                          {slot.recurrence_type ? (
                            <span className="badge badge-muted">
                              {slot.recurrence_type === 'weekly' ? 'Weekly' : 'Daily'}
                              {slot.recurrence_sequence ? ` · #${slot.recurrence_sequence}` : ''}
                            </span>
                          ) : (
                            <span className="text-muted">—</span>
                          )}
                        </td>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            {statusBadge(effectiveStatus)}
                            {effectiveStatus !== 'completed' && effectiveStatus !== 'cancelled' && (
                              <button
                                type="button"
                                className="btn btn-primary"
                                onClick={() =>
                                  openManualStart({
                                    type: 'schedule',
                                    id: slot.id,
                                    title: slot.title,
                                  })
                                }
                              >
                                Go live
                              </button>
                            )}
                          </div>
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          {['completed', 'cancelled', 'failed'].includes(effectiveStatus) ? (
                            <span className="text-muted">Locked</span>
                          ) : (
                            <ActionsDropdown
                              actions={[
                                { label: 'Edit', onClick: () => startScheduleEdit(slot) },
                                {
                                  label: 'Delete',
                                  onClick: () => handleCancelSlot(slot.id),
                                  variant: 'danger',
                                  disabled: effectiveStatus === 'in_progress',
                                },
                              ]}
                            />
                          )}
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              ) : (
                <div className="empty-state">
                  <h4>No slots scheduled</h4>
                  <p className="text-muted">Add a program or live window to populate the grid.</p>
                </div>
              )}
            </div>
          )}

          {isLibraryPage && (
            <div className="card">
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.75rem' }}>
                <div>
                  <h3>Program library</h3>
                  <p className="text-muted">Ready-to-air content.</p>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                  <label htmlFor="program_search" className="text-muted">
                    Search
                  </label>
                  <input
                    id="program_search"
                    type="search"
                    className="input"
                    value={programFilters.search}
                    onChange={handleProgramSearch}
                    placeholder="Title or description"
                  />
                  <label htmlFor="program_category_filter" className="text-muted">
                    Category
                  </label>
                  <select
                    id="program_category_filter"
                    className="input"
                    value={programFilters.category}
                    onChange={handleProgramCategoryFilter}
                  >
                    <option value="all">All</option>
                    {programCategoryOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <label htmlFor="program_sort_filter" className="text-muted">
                    Sort
                  </label>
                  <select
                    id="program_sort_filter"
                    className="input"
                    value={programFilters.sort}
                    onChange={handleProgramSort}
                  >
                    {programSortOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    className="btn btn-muted"
                    onClick={() => {
                      loadPrograms();
                      loadProgramOptions();
                    }}
                  >
                    Refresh
                  </button>
                </div>
              </div>

              {isSuperAdmin() && outOfSpecPrograms.length > 0 && (
                <div className="error-banner" style={{ marginBottom: '1rem' }}>
                  {outOfSpecPrograms.length} program{outOfSpecPrograms.length === 1 ? '' : 's'} are outside the 1:00:00
                  slot by more than {durationToleranceSeconds}s ({outOfSpecSummary.short} short, {outOfSpecSummary.long}{' '}
                  long) and need filler or a fade-out.
                </div>
              )}

              {loading.programs ? (
                <div>Loading programs…</div>
              ) : visiblePrograms.length ? (
                <>
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Title</th>
                        <th>Category</th>
                        <th>Media type</th>
                        <th>Usage</th>
                        <th>Duration</th>
                        <th>Uploaded</th>
                        <th style={{ textAlign: 'right' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {visiblePrograms.map((program) => (
                        <tr key={program.id}>
                          <td>
                            <strong>{program.title}</strong>
                            <p style={{ fontSize: '0.85rem' }}>{program.description || '—'}</p>
                          </td>
                          <td>{formatCategoryLabel(program.category)}</td>
                          <td style={{ textTransform: 'capitalize' }}>{program.media_type}</td>
                          <td>
                            {program.usage === 'filler' ? (
                              <span className="badge badge-muted">Filler</span>
                            ) : (
                              <span className="text-muted">Primary</span>
                            )}
                          </td>
                          <td>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                              <span>{formatDuration(program.duration_seconds)}</span>
                              {(() => {
                                const variance = getDurationVariance(
                                  program.duration_seconds,
                                  targetDurationSeconds,
                                  durationToleranceSeconds
                                );
                                if (!variance) return null;
                                return (
                                  <span className="badge badge-danger" style={{ marginLeft: '0.25rem' }}>
                                    {variance.label} by {formatDuration(variance.diff)}
                                  </span>
                                );
                              })()}
                              <button
                                type="button"
                                className="btn btn-primary"
                                onClick={() =>
                                  openManualStart({
                                    type: 'program',
                                    id: program.id,
                                    title: program.title,
                                  })
                                }
                              >
                                Go live
                              </button>
                            </div>
                          </td>
                          <td>{formatDateTime(program.created_at)}</td>
                          <td style={{ textAlign: 'right' }}>
                        <ActionsDropdown
                              actions={[
                                { label: 'Preview', onClick: () => setPreviewProgram(program) },
                                { label: 'Edit', onClick: () => startProgramEdit(program) },
                                { label: 'Delete', onClick: () => handleDeleteProgram(program.id), variant: 'danger' },
                              ]}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginTop: '1rem',
                      flexWrap: 'wrap',
                      gap: '0.75rem',
                    }}
                  >
                    <div className="text-muted">{programCountLabel}</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <label htmlFor="programs_per_page" className="text-muted">
                        Rows
                      </label>
                      <select
                        id="programs_per_page"
                        className="input"
                        value={programPagination.perPage}
                        onChange={handleProgramsPerPageChange}
                      >
                        {[25, 50, 100, 200, 500].map((size) => (
                          <option key={size} value={size}>
                            {size}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <button
                        type="button"
                        className="btn btn-muted"
                        onClick={() => handleProgramsPageChange(programPagination.page - 1)}
                        disabled={programPagination.page <= 1}
                      >
                        Prev
                      </button>
                      <span className="text-muted">
                        Page {programPagination.page} of {programPagination.lastPage}
                      </span>
                      <button
                        type="button"
                        className="btn btn-muted"
                        onClick={() => handleProgramsPageChange(programPagination.page + 1)}
                        disabled={programPagination.page >= programPagination.lastPage}
                      >
                        Next
                      </button>
                    </div>
                  </div>
                </>
              ) : (
                <div className="empty-state">
                  <h4>{libraryPrograms.length ? 'No matching programs' : 'No programs'}</h4>
                  <p className="text-muted">
                    {libraryPrograms.length ? 'Try adjusting the search or filters.' : 'Create a program to build your schedule.'}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {archiveQueueConfirmation.open && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.45)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 30,
            padding: '1rem',
          }}
        >
          <div
            className="card"
            style={{
              width: 'min(900px, 100%)',
              boxShadow: 'var(--shadow-lg)',
              padding: '1.25rem',
              position: 'relative',
              display: 'grid',
              gap: '0.85rem',
            }}
          >
            <h3 style={{ marginBottom: '0.15rem' }}>Confirm queue plan</h3>
            <p className="text-muted" style={{ marginBottom: 0 }}>
              {archiveQueueConfirmation.afterLastScheduled
                ? 'This will append selected items after your latest scheduled slot.'
                : 'This will queue selected items from the chosen queue start time.'}
            </p>
            <div className="pill-row">
              <span className="pill">
                Starts {formatDateTime(archiveQueueConfirmation.startAt)}
              </span>
              <span className="pill">
                Ends {formatDateTime(queuePlanLastItem?.endsAt)}
              </span>
              <span className="pill">
                Items {archiveQueueConfirmation.items.length}
              </span>
            </div>

            <div
              style={{
                border: '1px solid rgba(15, 23, 42, 0.1)',
                borderRadius: '10px',
                maxHeight: '44vh',
                overflowY: 'auto',
                padding: '0.5rem',
                display: 'grid',
                gap: '0.4rem',
              }}
            >
              {archiveQueueConfirmation.items.map((planned, index) => (
                <div
                  key={buildArchivePreviewItemKey(planned.item, index)}
                  style={{
                    border: '1px solid rgba(15, 23, 42, 0.08)',
                    borderRadius: '8px',
                    padding: '0.5rem 0.65rem',
                  }}
                >
                  <strong>{index + 1}. {planned.item.title || planned.item.identifier}</strong>
                  <div className="text-muted" style={{ fontSize: '0.9rem' }}>
                    {formatDateTime(planned.startsAt)} → {formatDateTime(planned.endsAt)} · {formatDuration(planned.durationSeconds)}
                  </div>
                </div>
              ))}
            </div>

            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button
                type="button"
                className="btn btn-muted"
                onClick={closeArchiveQueueConfirmation}
                disabled={queueingArchivePreview}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={confirmArchiveQueueToTimeline}
                disabled={queueingArchivePreview || archiveQueueConfirmation.items.length === 0}
              >
                {queueingArchivePreview ? 'Queueing…' : 'Confirm queue'}
              </button>
            </div>
          </div>
        </div>
      )}

      {manualStart.target && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.45)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 30,
            padding: '1rem',
          }}
        >
          <div
            className="card"
            style={{
              width: 'min(520px, 100%)',
              boxShadow: 'var(--shadow-lg)',
              padding: '1.5rem',
              position: 'relative',
            }}
          >
            <h3 style={{ marginBottom: '0.25rem' }}>Start program</h3>
            <p className="text-muted" style={{ marginBottom: '0.75rem' }}>
              {manualStart.target.title}
            </p>

            <form onSubmit={submitManualStart} className="modal-form" style={{ display: 'grid', gap: '1rem' }}>
              <div className="field">
                <label htmlFor="manual_starts_at">Start time</label>
                <input
                  id="manual_starts_at"
                  name="starts_at"
                  type="datetime-local"
                  className="input"
                  value={manualStart.starts_at}
                  onChange={handleManualStartChange}
                  step="0.001"
                  disabled={manualStart.immediate}
                  required={!manualStart.immediate}
                />
              </div>

              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <input
                  type="checkbox"
                  name="immediate"
                  checked={manualStart.immediate}
                  onChange={handleManualStartChange}
                />
                <span>Start playback immediately</span>
              </label>

              {manualStart.current && playbackConflict && (
                <div className="error-banner" style={{ display: 'grid', gap: '0.5rem' }}>
                  <div>
                    A program is already playing:{' '}
                    <strong>{manualStart.current.title ?? manualStart.current.program?.title ?? 'Current slot'}</strong>.
                  </div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <input
                      type="checkbox"
                      name="force"
                      checked={manualStart.force}
                      onChange={handleManualStartChange}
                    />
                    <span>Override current program and start this one now</span>
                  </label>
                </div>
              )}

              {manualStart.error && <div className="error-banner">{manualStart.error}</div>}

              <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                <button type="button" className="btn btn-muted" onClick={closeManualStart} disabled={manualStart.loading}>
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={manualStart.loading || (playbackConflict && !manualStart.force)}
                >
                  {manualStart.loading ? 'Starting…' : manualStart.immediate ? 'Start now' : 'Schedule start'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {previewProgram && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.45)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 30,
            padding: '1rem',
          }}
        >
          <div
            className="card"
            style={{
              width: 'min(620px, 100%)',
              boxShadow: 'var(--shadow-lg)',
              padding: '1.5rem',
              position: 'relative',
              display: 'grid',
              gap: '0.75rem',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem' }}>
              <div>
                <h3 style={{ margin: 0 }}>Program preview</h3>
                <p className="text-muted" style={{ margin: '0.3rem 0 0' }}>
                  {previewProgram.title}
                </p>
              </div>
              <button type="button" className="btn btn-muted" onClick={() => setPreviewProgram(null)}>
                Close
              </button>
            </div>
            {(() => {
              const preview = buildProgramPreview(previewProgram);
              if (preview?.isVideo) {
                return (
                  <video
                    key={preview.mediaUrl}
                    src={preview.mediaUrl}
                    poster={previewProgram.thumbnail_url}
                    style={{ width: '100%', maxHeight: 360, background: '#000', borderRadius: '10px' }}
                    controls
                    muted
                    playsInline
                  />
                );
              }
              if (preview?.isImage) {
                return (
                  <img
                    src={preview.mediaUrl}
                    alt={previewProgram.title}
                    style={{ width: '100%', maxHeight: 360, objectFit: 'contain', borderRadius: '10px' }}
                  />
                );
              }
              if (preview?.isEmbeddable) {
                return (
                  <iframe
                    key={preview.odyseeEmbed ?? preview.mediaUrl}
                    src={preview.odyseeEmbed ?? preview.mediaUrl}
                    title={previewProgram.title}
                    style={{ width: '100%', minHeight: 260, borderRadius: '10px', background: '#000' }}
                    frameBorder="0"
                    allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                );
              }
              if (previewProgram.thumbnail_url) {
                return (
                  <img
                    src={previewProgram.thumbnail_url}
                    alt={previewProgram.title}
                    style={{ width: '100%', maxHeight: 360, objectFit: 'contain', borderRadius: '10px' }}
                  />
                );
              }
              return <div className="text-muted">No preview available for this media type.</div>;
            })()}
            {previewProgram.description && (
              <p className="text-muted" style={{ margin: 0 }}>
                {previewProgram.description}
              </p>
            )}
          </div>
        </div>
      )}

      <MediaLibraryPickerModal
        open={programThumbnailPickerOpen}
        onClose={() => setProgramThumbnailPickerOpen(false)}
        onSelect={(url) => {
          if (!url) return;
          setProgramForm((prev) => ({ ...prev, thumbnail_url: url }));
          setFeedback((prev) => ({
            ...prev,
            error: '',
            program: 'Thumbnail selected from media library.',
          }));
        }}
      />
    </section>
  );
};

export default ProgrammingWorkspace;
